import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import {
  MessageIcon, PlusIcon, TrashIcon, CheckIcon, CloseIcon, ArrowIcon,
  SearchIcon, SaveIcon, CalendarIcon, BookIcon, ProfileIcon, HomeIcon,
  StarIcon, ClockIcon, UsersIcon, SparkleIcon, PlayIcon, PauseIcon,
  ShareIcon, ChevronIcon, InfoIcon, RefreshIcon, FeelIcon, NeedIcon, GoldIcon, ObserveIcon,
  StackIcon, FlowIcon, FireIcon, SunriseIcon, WeekIcon, MatrixIcon, BacklogIcon, AllTasksIcon, RecurIcon, PhotoIcon
} from './BespokeIcons';

// Import M.ai.ry Wisdom Toolbar for soul-centered project guidance
import { MairyFloatingButton, MairyWisdomPanel } from './MairyWisdomToolbar';

// Import shared constants from modular sketch components
import {
  QUADRANTS,
  PRIORITY_COLORS,
  PRIORITY_FLAGS,
  ITEM_SHAPES,
  BOARD_COLUMNS,
  PROJECT_STATUS
} from './sketch/constants';

// Import NVC Canvas Overlay for OFNR quadrant visualization
import { NVCCanvasOverlay, NVCOverlayToggle, NVCQuickInput, OFNR_QUADRANTS } from './NVCCanvasOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Project Types with icons and colors
const PROJECT_TYPES = {
  conflict: {
    id: 'conflict',
    name: 'Conflict Resolution',
    description: 'Transform conflict into understanding using the ElchmE process',
    icon: GoldIcon,
    color: '#F59E0B',
    gradient: 'from-amber-500/20 to-orange-500/10',
    borderColor: 'border-amber-500/30',
  },
  family: {
    id: 'family',
    name: 'Family Project',
    description: 'Organize family matters, events, and communications',
    icon: UsersIcon,
    color: '#EC4899',
    gradient: 'from-pink-500/20 to-rose-500/10',
    borderColor: 'border-pink-500/30',
  },
  work: {
    id: 'work',
    name: 'Work Project',
    description: 'Professional tasks, team collaboration, and deadlines',
    icon: StarIcon,
    color: '#3B82F6',
    gradient: 'from-blue-500/20 to-indigo-500/10',
    borderColor: 'border-blue-500/30',
  },
  personal: {
    id: 'personal',
    name: 'Personal Growth',
    description: 'Self-improvement, goals, and personal reflections',
    icon: SparkleIcon,
    color: '#8B5CF6',
    gradient: 'from-purple-500/20 to-violet-500/10',
    borderColor: 'border-purple-500/30',
  },
  custom: {
    id: 'custom',
    name: 'Custom Project',
    description: 'Create your own project type',
    icon: BookIcon,
    color: '#10B981',
    gradient: 'from-emerald-500/20 to-teal-500/10',
    borderColor: 'border-emerald-500/30',
  },
};

// Project Templates
const PROJECT_TEMPLATES = {
  conflict_resolution: {
    name: 'Conflict Resolution',
    type: 'conflict',
    description: 'Use the OFNR process to resolve conflicts',
    defaultItems: [
      { type: 'note', content: 'Step 1: Observe - What happened? (facts only)', quadrant: 'urgentImportant' },
      { type: 'note', content: 'Step 2: Feel - What feelings came up?', quadrant: 'urgentImportant' },
      { type: 'note', content: 'Step 3: Need - What needs are beneath those feelings?', quadrant: 'importantNotUrgent' },
      { type: 'note', content: 'Step 4: Gold - What resolution honors everyone?', quadrant: 'importantNotUrgent' },
    ],
  },
  family_meeting: {
    name: 'Family Meeting',
    type: 'family',
    description: 'Organize a family discussion or meeting',
    defaultItems: [
      { type: 'note', content: 'Agenda items to discuss', quadrant: 'urgentImportant' },
      { type: 'note', content: 'Everyone\'s feelings and concerns', quadrant: 'importantNotUrgent' },
      { type: 'note', content: 'Action items and responsibilities', quadrant: 'urgentNotImportant' },
    ],
  },
  project_planning: {
    name: 'Project Planning',
    type: 'work',
    description: 'Plan and organize a work project',
    defaultItems: [
      { type: 'todo', content: 'Define project goals and scope', quadrant: 'urgentImportant', priority: 'high' },
      { type: 'todo', content: 'Identify key stakeholders', quadrant: 'urgentImportant', priority: 'high' },
      { type: 'todo', content: 'Create timeline and milestones', quadrant: 'importantNotUrgent', priority: 'medium' },
      { type: 'todo', content: 'Allocate resources', quadrant: 'importantNotUrgent', priority: 'medium' },
    ],
  },
  blank: {
    name: 'Blank Canvas',
    type: 'custom',
    description: 'Start with an empty project',
    defaultItems: [],
  },
};

// ============================================
// PROJECT CARD COMPONENT
// ============================================
function ProjectCard({ project, onSelect, onDelete, onStatusChange }) {
  const typeInfo = PROJECT_TYPES[project.type] || PROJECT_TYPES.custom;
  const statusInfo = PROJECT_STATUS[project.status] || PROJECT_STATUS.active;
  const IconComponent = typeInfo.icon;
  
  const progress = project.progress || 0;
  const participantsCount = project.participants?.length || 0;
  const messagesCount = project.messages_count || 0;
  const itemsCount = project.items_count || 0;

  return (
    <Card 
      className={`group cursor-pointer transition-all duration-300 hover:scale-[1.02] bg-gradient-to-br ${typeInfo.gradient} border ${typeInfo.borderColor} hover:shadow-xl hover:shadow-${typeInfo.color}/10`}
      onClick={() => onSelect(project)}
      data-testid={`project-card-${project._id}`}
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${typeInfo.color}20`, border: `1px solid ${typeInfo.color}40` }}
            >
              <IconComponent size={24} color={typeInfo.color} />
            </div>
            <div>
              <h3 className="font-semibold text-white text-lg group-hover:text-amber-400 transition-colors line-clamp-1">
                {project.name}
              </h3>
              <p className="text-white/50 text-sm">{typeInfo.name}</p>
            </div>
          </div>
          <Badge 
            className={`${statusInfo.bgColor} border-0`}
            style={{ color: statusInfo.color }}
          >
            {statusInfo.label}
          </Badge>
        </div>

        {/* Description */}
        {project.description && (
          <p className="text-white/60 text-sm mb-4 line-clamp-2">{project.description}</p>
        )}

        {/* Progress Bar */}
        {progress > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-white/40 mb-1">
              <span>Progress</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="h-1.5" />
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 text-white/40 text-sm">
          <div className="flex items-center gap-1.5">
            <MessageIcon size={14} color="currentColor" />
            <span>{messagesCount}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <CheckIcon size={14} color="currentColor" />
            <span>{itemsCount}</span>
          </div>
          {participantsCount > 0 && (
            <div className="flex items-center gap-1.5">
              <UsersIcon size={14} color="currentColor" />
              <span>{participantsCount}</span>
            </div>
          )}
          <div className="flex-1" />
          <div className="flex items-center gap-1.5 text-white/30">
            <ClockIcon size={12} color="currentColor" />
            <span>{new Date(project.updated_at || project.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// CREATE PROJECT DIALOG
// ============================================
function CreateProjectDialog({ open, onOpenChange, onCreateProject, userId }) {
  const [step, setStep] = useState(1); // 1: type, 2: details, 3: template
  const [selectedType, setSelectedType] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!projectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }

    setIsCreating(true);
    try {
      await onCreateProject({
        name: projectName,
        description: projectDescription,
        type: selectedType,
        template: selectedTemplate,
        user_id: userId,
      });
      
      // Reset form
      setStep(1);
      setSelectedType(null);
      setSelectedTemplate(null);
      setProjectName('');
      setProjectDescription('');
      onOpenChange(false);
      toast.success('Project created!');
    } catch (error) {
      toast.error('Failed to create project');
    } finally {
      setIsCreating(false);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleNext = () => {
    if (step === 1 && selectedType) setStep(2);
    else if (step === 2 && projectName.trim()) setStep(3);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
            {step === 1 && 'Choose Project Type'}
            {step === 2 && 'Project Details'}
            {step === 3 && 'Start with a Template?'}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {step === 1 && 'What kind of project are you creating?'}
            {step === 2 && 'Give your project a name and description'}
            {step === 3 && 'Choose a template to get started quickly, or start blank'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {/* Step 1: Project Type */}
          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.values(PROJECT_TYPES).map((type) => {
                const IconComponent = type.icon;
                const isSelected = selectedType === type.id;
                return (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type.id)}
                    className={`p-4 rounded-xl border transition-all text-left ${
                      isSelected 
                        ? `bg-gradient-to-br ${type.gradient} ${type.borderColor} ring-2 ring-offset-2 ring-offset-zinc-900`
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                    }`}
                    style={isSelected ? { ringColor: type.color } : {}}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${type.color}20` }}
                      >
                        <IconComponent size={20} color={type.color} />
                      </div>
                      <span className="font-medium text-white">{type.name}</span>
                    </div>
                    <p className="text-white/50 text-sm">{type.description}</p>
                  </button>
                );
              })}
            </div>
          )}

          {/* Step 2: Project Details */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="text-white/70 text-sm mb-2 block">Project Name *</label>
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="My Awesome Project"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-white/70 text-sm mb-2 block">Description (optional)</label>
                <Textarea
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  placeholder="What is this project about?"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[100px]"
                />
              </div>
            </div>
          )}

          {/* Step 3: Template Selection */}
          {step === 3 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(PROJECT_TEMPLATES)
                .filter(([key, template]) => template.type === selectedType || key === 'blank')
                .map(([key, template]) => {
                  const isSelected = selectedTemplate === key;
                  const typeInfo = PROJECT_TYPES[template.type] || PROJECT_TYPES.custom;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedTemplate(key)}
                      className={`p-4 rounded-xl border transition-all text-left ${
                        isSelected 
                          ? `bg-gradient-to-br ${typeInfo.gradient} ${typeInfo.borderColor} ring-2 ring-amber-500 ring-offset-2 ring-offset-zinc-900`
                          : 'bg-white/5 border-white/10 hover:border-white/20'
                      }`}
                    >
                      <div className="font-medium text-white mb-1">{template.name}</div>
                      <p className="text-white/50 text-sm">{template.description}</p>
                      {template.defaultItems?.length > 0 && (
                        <p className="text-amber-400/70 text-xs mt-2">
                          Includes {template.defaultItems.length} starter items
                        </p>
                      )}
                    </button>
                  );
                })}
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-3">
          {step > 1 && (
            <Button variant="ghost" onClick={handleBack} className="text-white/60">
              <ArrowIcon size={16} color="currentColor" direction="left" className="mr-2" />
              Back
            </Button>
          )}
          <div className="flex-1" />
          {step < 3 ? (
            <Button 
              onClick={handleNext}
              disabled={step === 1 && !selectedType}
              className="bg-amber-500 hover:bg-amber-400 text-black"
            >
              Continue
              <ArrowIcon size={16} color="#000" direction="right" className="ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleCreate}
              disabled={isCreating}
              className="bg-amber-500 hover:bg-amber-400 text-black"
            >
              {isCreating ? (
                <RefreshIcon size={16} color="#000" className="animate-spin mr-2" />
              ) : (
                <PlusIcon size={16} color="#000" className="mr-2" />
              )}
              Create Project
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// MESSAGE BUBBLE COMPONENT - Facebook Messenger + iPhone style with full features
// ============================================

function MessageBubble({ 
  message, 
  isOwn, 
  onDragToCanvas, 
  selected, 
  onSelect,
  onUpdateMessage,
  onConvertToTodo,
  teamMembers = [],
  compact = false
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const [showAssignMenu, setShowAssignMenu] = useState(false);
  const [showKeywordInput, setShowKeywordInput] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  
  const handleDragStart = (e) => {
    e.dataTransfer.setData('message', JSON.stringify(message));
    setIsDragging(true);
  };
  
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleAddKeyword = () => {
    if (newKeyword.trim() && onUpdateMessage) {
      const keywords = [...(message.keywords || []), newKeyword.trim()];
      onUpdateMessage(message._id, { keywords });
      setNewKeyword('');
      setShowKeywordInput(false);
    }
  };

  const handleRemoveKeyword = (kw) => {
    if (onUpdateMessage) {
      const keywords = (message.keywords || []).filter(k => k !== kw);
      onUpdateMessage(message._id, { keywords });
    }
  };

  const handleSetPriority = (priority) => {
    if (onUpdateMessage) {
      onUpdateMessage(message._id, { priority });
      setShowPriorityMenu(false);
    }
  };

  const handleAssign = (member) => {
    if (onUpdateMessage) {
      onUpdateMessage(message._id, { assignee: member });
      setShowAssignMenu(false);
    }
  };

  const priorityInfo = message.priority ? PRIORITY_FLAGS[message.priority] : null;

  return (
    <div 
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3 group relative`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => { setShowActions(false); setShowPriorityMenu(false); setShowAssignMenu(false); }}
    >
      {/* Avatar for non-own messages */}
      {!isOwn && !compact && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0">
          {(message.sender_name || 'U')[0].toUpperCase()}
        </div>
      )}
      
      <div className="flex flex-col max-w-[75%]">
        {/* Priority flag */}
        {priorityInfo && (
          <div className={`self-start mb-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${priorityInfo.bgColor}`} style={{ color: priorityInfo.color }}>
            {priorityInfo.label}
          </div>
        )}
        
        {/* Message bubble */}
        <div 
          className={`
            px-4 py-2.5 rounded-2xl cursor-grab active:cursor-grabbing
            transition-all duration-200 relative
            ${isDragging ? 'opacity-50 scale-95' : ''}
            ${selected ? 'ring-2 ring-amber-400 ring-offset-1 ring-offset-transparent' : ''}
            ${message.isTodo ? 'border-l-4 border-emerald-500' : ''}
            ${isOwn 
              ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black rounded-br-md' 
              : 'bg-white/10 text-white rounded-bl-md'
            }
          `}
          onClick={() => onSelect && onSelect(message)}
        >
          {!isOwn && !compact && (
            <p className="text-xs font-medium text-white/70 mb-1">{message.sender_name || 'User'}</p>
          )}
          
          <p className={`text-sm leading-relaxed ${message.isTodo && message.isCompleted ? 'line-through opacity-60' : ''}`}>
            {message.content}
          </p>
          
          {/* Keywords/Tags */}
          {message.keywords?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {message.keywords.map((kw, idx) => (
                <span 
                  key={idx} 
                  className={`text-[10px] px-1.5 py-0.5 rounded-full ${isOwn ? 'bg-black/20 text-black/70' : 'bg-white/10 text-white/60'}`}
                >
                  #{kw}
                  {showActions && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleRemoveKeyword(kw); }}
                      className="ml-1 hover:text-red-400"
                    >×</button>
                  )}
                </span>
              ))}
            </div>
          )}
          
          {/* Assignee */}
          {message.assignee && (
            <div className={`flex items-center gap-1 mt-2 text-[10px] ${isOwn ? 'text-black/60' : 'text-white/40'}`}>
              <ProfileIcon size={10} color="currentColor" />
              <span>{message.assignee}</span>
            </div>
          )}
          
          {/* Timestamp and status */}
          <div className={`flex items-center justify-between mt-1.5 text-[10px] ${isOwn ? 'text-black/50' : 'text-white/30'}`}>
            <span>{new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            {message.isTodo && (
              <span className="flex items-center gap-1">
                <CheckIcon size={10} color={message.isCompleted ? '#10B981' : 'currentColor'} />
                {message.isCompleted ? 'Done' : 'Todo'}
              </span>
            )}
          </div>
        </div>
        
        {/* Action bar - appears on hover */}
        {showActions && (
          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'self-end' : 'self-start'}`}>
            {/* Convert to Todo */}
            <button
              onClick={() => onConvertToTodo && onConvertToTodo(message)}
              className={`p-1.5 rounded-full ${message.isTodo ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-white/40 hover:text-emerald-400 hover:bg-emerald-500/10'} transition-colors`}
              title={message.isTodo ? 'Already a todo' : 'Convert to todo'}
            >
              <CheckIcon size={12} color="currentColor" />
            </button>
            
            {/* Priority flag */}
            <div className="relative">
              <button
                onClick={() => setShowPriorityMenu(!showPriorityMenu)}
                className={`p-1.5 rounded-full bg-white/5 transition-colors ${message.priority ? 'text-amber-400' : 'text-white/40 hover:text-amber-400'}`}
                title="Set priority"
              >
                <StarIcon size={12} color="currentColor" />
              </button>
              {showPriorityMenu && (
                <div className="absolute bottom-full left-0 mb-1 bg-zinc-900 border border-white/10 rounded-lg p-1 shadow-xl z-50">
                  {Object.entries(PRIORITY_FLAGS).map(([p, info]) => (
                    <button
                      key={p}
                      onClick={() => handleSetPriority(Number(p))}
                      className={`w-full text-left px-2 py-1 text-xs rounded hover:bg-white/5 ${message.priority === Number(p) ? info.bgColor : ''}`}
                      style={{ color: info.color }}
                    >
                      {info.label}
                    </button>
                  ))}
                  {message.priority && (
                    <button
                      onClick={() => handleSetPriority(null)}
                      className="w-full text-left px-2 py-1 text-xs rounded hover:bg-white/5 text-white/40"
                    >
                      Clear priority
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Assign to team member */}
            <div className="relative">
              <button
                onClick={() => setShowAssignMenu(!showAssignMenu)}
                className={`p-1.5 rounded-full bg-white/5 transition-colors ${message.assignee ? 'text-purple-400' : 'text-white/40 hover:text-purple-400'}`}
                title="Assign to team member"
              >
                <ProfileIcon size={12} color="currentColor" />
              </button>
              {showAssignMenu && (
                <div className="absolute bottom-full left-0 mb-1 bg-zinc-900 border border-white/10 rounded-lg p-1 shadow-xl z-50 min-w-[120px]">
                  {teamMembers.length > 0 ? teamMembers.map((member, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleAssign(member.name || member)}
                      className="w-full text-left px-2 py-1 text-xs rounded hover:bg-white/5 text-white/70"
                    >
                      {member.name || member}
                    </button>
                  )) : (
                    <>
                      <button onClick={() => handleAssign('Me')} className="w-full text-left px-2 py-1 text-xs rounded hover:bg-white/5 text-white/70">Me</button>
                      <button onClick={() => handleAssign('Partner')} className="w-full text-left px-2 py-1 text-xs rounded hover:bg-white/5 text-white/70">Partner</button>
                      <button onClick={() => handleAssign('Team')} className="w-full text-left px-2 py-1 text-xs rounded hover:bg-white/5 text-white/70">Team</button>
                    </>
                  )}
                  {message.assignee && (
                    <button
                      onClick={() => handleAssign(null)}
                      className="w-full text-left px-2 py-1 text-xs rounded hover:bg-white/5 text-red-400/70 border-t border-white/5 mt-1"
                    >
                      Unassign
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Add keyword */}
            <div className="relative">
              <button
                onClick={() => setShowKeywordInput(!showKeywordInput)}
                className="p-1.5 rounded-full bg-white/5 text-white/40 hover:text-cyan-400 transition-colors"
                title="Add keyword/tag"
              >
                <span className="text-xs font-bold">#</span>
              </button>
              {showKeywordInput && (
                <div className="absolute bottom-full left-0 mb-1 bg-zinc-900 border border-white/10 rounded-lg p-2 shadow-xl z-50">
                  <input
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddKeyword(); }}
                    placeholder="Add tag..."
                    className="w-24 px-2 py-1 text-xs bg-white/5 border border-white/10 rounded text-white placeholder:text-white/30"
                    autoFocus
                  />
                </div>
              )}
            </div>
            
            {/* Drag indicator */}
            <div className="p-1.5 rounded-full bg-white/5 text-white/40">
              <ArrowIcon size={12} color="currentColor" direction="right" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// MESSAGING PANEL COMPONENT
// ============================================
function MessagingPanel({ 
  projectId, 
  userId, 
  userName,
  messages, 
  onSendMessage, 
  onRefresh,
  selectedMessages,
  onSelectMessage,
  isFullscreen,
  onToggleFullscreen,
  viewMode = 'all',
  onViewModeChange,
  onUpdateMessage,
  onConvertToTodo,
  teamMembers = []
}) {
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localViewMode, setLocalViewMode] = useState(viewMode);
  const [filterKeyword, setFilterKeyword] = useState(null);
  const [filterPriority, setFilterPriority] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setIsLoading(true);
    try {
      await onSendMessage(newMessage);
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  // Get all unique keywords from messages
  const getAllKeywords = () => {
    const keywords = new Set();
    messages.forEach(msg => {
      (msg.keywords || []).forEach(kw => keywords.add(kw));
    });
    return Array.from(keywords);
  };

  // Group messages by date
  const getMessagesByDate = () => {
    const groups = {};
    messages.forEach(msg => {
      const date = new Date(msg.created_at || msg.timestamp).toLocaleDateString();
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  // Group messages by thread/category (based on content patterns)
  const getMessagesByThread = () => {
    const threads = {
      feelings: [],
      needs: [],
      observations: [],
      requests: [],
      other: []
    };
    messages.forEach(msg => {
      const content = (msg.content || '').toLowerCase();
      if (content.includes('feel') || content.includes('emotion')) {
        threads.feelings.push(msg);
      } else if (content.includes('need') || content.includes('want')) {
        threads.needs.push(msg);
      } else if (content.includes('notice') || content.includes('observe') || content.includes('see')) {
        threads.observations.push(msg);
      } else if (content.includes('request') || content.includes('would you') || content.includes('could you')) {
        threads.requests.push(msg);
      } else {
        threads.other.push(msg);
      }
    });
    return threads;
  };

  const renderMessages = () => {
    // Filter by keyword if selected
    let filteredMsgs = messages;
    if (filterKeyword) {
      filteredMsgs = messages.filter(msg => (msg.keywords || []).includes(filterKeyword));
    }
    if (filterPriority) {
      filteredMsgs = filteredMsgs.filter(msg => msg.priority === filterPriority);
    }

    if (localViewMode === 'byDate') {
      const grouped = {};
      filteredMsgs.forEach(msg => {
        const date = new Date(msg.created_at || msg.timestamp).toLocaleDateString();
        if (!grouped[date]) grouped[date] = [];
        grouped[date].push(msg);
      });
      return Object.entries(grouped).map(([date, msgs]) => (
        <div key={date} className="mb-4">
          <div className="sticky top-0 bg-black/60 backdrop-blur-sm text-center py-1 text-xs text-white/40 rounded mb-2">
            {date}
          </div>
          {msgs.map((msg, idx) => (
            <MessageBubble 
              key={msg._id || idx}
              message={msg}
              isOwn={msg.sender_id === userId}
              selected={selectedMessages?.includes(msg._id)}
              onSelect={onSelectMessage}
              onUpdateMessage={onUpdateMessage}
              onConvertToTodo={onConvertToTodo}
              teamMembers={teamMembers}
            />
          ))}
        </div>
      ));
    }
    
    if (localViewMode === 'byThread') {
      const threads = getMessagesByThread();
      const threadLabels = {
        feelings: { label: 'Feelings', color: '#EC4899', icon: FeelIcon },
        needs: { label: 'Needs', color: '#8B5CF6', icon: NeedIcon },
        observations: { label: 'Observations', color: '#06B6D4', icon: ObserveIcon },
        requests: { label: 'Requests', color: '#F59E0B', icon: GoldIcon },
        other: { label: 'Other', color: '#6B7280', icon: MessageIcon }
      };
      
      return Object.entries(threads).filter(([_, msgs]) => msgs.length > 0).map(([thread, msgs]) => {
        const info = threadLabels[thread];
        const IconComponent = info.icon;
        return (
          <div key={thread} className="mb-4">
            <div className="flex items-center gap-2 mb-2 px-2 py-1 rounded" style={{ backgroundColor: `${info.color}15` }}>
              <IconComponent size={14} color={info.color} />
              <span className="text-xs font-medium" style={{ color: info.color }}>{info.label}</span>
              <Badge className="ml-auto text-[10px]" style={{ backgroundColor: `${info.color}30`, color: info.color }}>
                {msgs.length}
              </Badge>
            </div>
            {msgs.map((msg, idx) => (
              <MessageBubble 
                key={msg._id || idx}
                message={msg}
                isOwn={msg.sender_id === userId}
                selected={selectedMessages?.includes(msg._id)}
                onSelect={onSelectMessage}
                onUpdateMessage={onUpdateMessage}
                onConvertToTodo={onConvertToTodo}
                teamMembers={teamMembers}
                compact={true}
              />
            ))}
          </div>
        );
      });
    }
    
    // Default - all messages
    return filteredMsgs.map((msg, idx) => (
      <MessageBubble 
        key={msg._id || idx}
        message={msg}
        isOwn={msg.sender_id === userId}
        selected={selectedMessages?.includes(msg._id)}
        onSelect={onSelectMessage}
        onUpdateMessage={onUpdateMessage}
        onConvertToTodo={onConvertToTodo}
        teamMembers={teamMembers}
      />
    ));
  };

  return (
    <div className={`
      flex flex-col bg-black/40 backdrop-blur-xl rounded-2xl border border-white/10
      ${isFullscreen ? 'fixed inset-4 z-50' : 'h-full'}
    `}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <MessageIcon size={20} color="#FBBF24" />
          <span className="font-medium text-white">Messages</span>
          {messages.length > 0 && (
            <Badge variant="secondary" className="bg-amber-500/20 text-amber-400">
              {messages.length}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onRefresh}
            className="text-white/50 hover:text-white"
          >
            <RefreshIcon size={16} color="currentColor" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onToggleFullscreen}
            className="text-white/50 hover:text-white md:hidden"
          >
            {isFullscreen ? <CloseIcon size={16} color="currentColor" /> : <ArrowIcon size={16} color="currentColor" direction="up" />}
          </Button>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-white/5">
        {[
          { id: 'all', label: 'All' },
          { id: 'byDate', label: 'By Date' },
          { id: 'byThread', label: 'By Thread' }
        ].map(mode => (
          <button
            key={mode.id}
            onClick={() => {
              setLocalViewMode(mode.id);
              onViewModeChange && onViewModeChange(mode.id);
            }}
            className={`px-3 py-1 text-xs rounded-full transition-colors ${
              localViewMode === mode.id 
                ? 'bg-amber-500/20 text-amber-400' 
                : 'text-white/40 hover:text-white/60'
            }`}
          >
            {mode.label}
          </button>
        ))}
      </div>

      {/* Messages Area */}
      <ScrollArea ref={scrollRef} className="flex-1 p-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <MessageIcon size={48} color="rgba(255,255,255,0.2)" />
            <p className="text-white/40 mt-4">No messages yet</p>
            <p className="text-white/20 text-sm mt-1">Start a conversation</p>
          </div>
        ) : (
          renderMessages()
        )}
        )}
      </ScrollArea>

      {/* Input Area */}
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Type a message..."
            className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-full px-4"
          />
          <Button 
            onClick={handleSend}
            disabled={isLoading || !newMessage.trim()}
            className="rounded-full w-10 h-10 p-0 bg-amber-500 hover:bg-amber-400"
          >
            <ArrowIcon size={18} color="#000" direction="up" />
          </Button>
        </div>
        {selectedMessages?.length > 0 && (
          <p className="text-xs text-amber-400 mt-2 text-center">
            {selectedMessages.length} message(s) selected - drag to canvas
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================
// CANVAS ITEM COMPONENT
// ============================================
function CanvasItem({ 
  item, 
  onUpdate, 
  onDelete, 
  isSelected, 
  onSelect,
  quadrantMode 
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(item.content || '');
  const [position, setPosition] = useState({ x: item.x || 50, y: item.y || 50 });
  const startPosRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    if (isEditing) return;
    setIsDragging(true);
    onSelect(item._id);
    startPosRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const newX = e.clientX - startPosRef.current.x;
    const newY = e.clientY - startPosRef.current.y;
    setPosition({ x: Math.max(0, newX), y: Math.max(0, newY) });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      let quadrant = item.quadrant;
      if (quadrantMode) {
        const canvasWidth = window.innerWidth * 0.6;
        const canvasHeight = window.innerHeight * 0.7;
        const midX = canvasWidth / 2;
        const midY = canvasHeight / 2;
        
        if (position.x < midX && position.y < midY) quadrant = 'urgentImportant';
        else if (position.x >= midX && position.y < midY) quadrant = 'importantNotUrgent';
        else if (position.x < midX && position.y >= midY) quadrant = 'notUrgentNotImportant';
        else quadrant = 'urgentNotImportant';
      }
      onUpdate(item._id, { x: position.x, y: position.y, quadrant });
    }
  }, [isDragging, position, item._id, item.quadrant, onUpdate, quadrantMode]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleSaveEdit = () => {
    onUpdate(item._id, { content: editContent });
    setIsEditing(false);
  };

  const getQuadrantColor = () => {
    if (item.quadrant && QUADRANTS[item.quadrant]) {
      return QUADRANTS[item.quadrant].color;
    }
    return item.priority ? PRIORITY_COLORS[item.priority] : '#FBBF24';
  };

  return (
    <div
      className={`
        absolute select-none transition-all duration-200
        ${isDragging ? 'z-50 cursor-grabbing scale-105' : 'cursor-grab'}
        ${isSelected ? 'ring-2 ring-white/50' : ''}
      `}
      style={{
        left: position.x,
        top: position.y,
        transform: isDragging ? 'rotate(-2deg)' : 'rotate(0deg)',
      }}
      onMouseDown={handleMouseDown}
    >
      <div 
        className="w-48 min-h-[80px] rounded-lg shadow-xl backdrop-blur-sm border border-white/20 overflow-hidden"
        style={{ 
          backgroundColor: `${getQuadrantColor()}20`,
          borderColor: `${getQuadrantColor()}40`,
          boxShadow: isDragging ? `0 20px 40px ${getQuadrantColor()}30` : `0 4px 12px rgba(0,0,0,0.3)`
        }}
      >
        <div 
          className="flex items-center justify-between px-3 py-2"
          style={{ backgroundColor: `${getQuadrantColor()}30` }}
        >
          <div className="flex items-center gap-2">
            {item.type === 'message' && <MessageIcon size={14} color={getQuadrantColor()} />}
            {item.type === 'todo' && <CheckIcon size={14} color={getQuadrantColor()} />}
            {item.type === 'note' && <BookIcon size={14} color={getQuadrantColor()} />}
            {item.type === 'link' && <ShareIcon size={14} color={getQuadrantColor()} />}
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-6 h-6 p-0"
              onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
            >
              <SparkleIcon size={12} color="#fff" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-6 h-6 p-0 hover:bg-red-500/20"
              onClick={(e) => { e.stopPropagation(); onDelete(item._id); }}
            >
              <TrashIcon size={12} color="#EF4444" />
            </Button>
          </div>
        </div>
        
        <div className="p-3">
          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="min-h-[60px] text-sm bg-black/20 border-white/10 text-white"
                autoFocus
              />
              <div className="flex gap-1">
                <Button size="sm" onClick={handleSaveEdit} className="flex-1 h-7">
                  <CheckIcon size={12} color="#10B981" className="mr-1" /> Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)} className="h-7">
                  <CloseIcon size={12} color="#9CA3AF" />
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-white/90 line-clamp-4">{item.content}</p>
          )}
          
          {item.thumbnail && (
            <img 
              src={item.thumbnail} 
              alt="Thumbnail" 
              className="mt-2 w-full h-20 object-cover rounded"
            />
          )}
          
          {item.keywords?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {item.keywords.map((kw, idx) => (
                <Badge key={idx} variant="outline" className="text-[10px] px-1 py-0">
                  {kw}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// ENHANCED CANVAS ITEM - Compact with full functionality
// ============================================
function EnhancedCanvasItem({ 
  item, 
  onUpdate, 
  onDelete, 
  isSelected, 
  onSelect,
  quadrantMode,
  teamMembers = []
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(item.content || '');
  const [position, setPosition] = useState({ x: item.x || 50, y: item.y || 50 });
  const startPosRef = useRef({ x: 0, y: 0 });
  
  const shape = ITEM_SHAPES[item.shape] || ITEM_SHAPES.sticky;
  
  const handleMouseDown = (e) => {
    if (isEditing || e.target.closest('button') || e.target.closest('input')) return;
    setIsDragging(true);
    onSelect(item._id);
    startPosRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const newX = e.clientX - startPosRef.current.x;
    const newY = e.clientY - startPosRef.current.y;
    setPosition({ x: Math.max(0, newX), y: Math.max(0, newY) });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      let quadrant = item.quadrant;
      if (quadrantMode) {
        const canvasWidth = window.innerWidth * 0.6;
        const canvasHeight = window.innerHeight * 0.7;
        const midX = canvasWidth / 2;
        const midY = canvasHeight / 2;
        
        if (position.x < midX && position.y < midY) quadrant = 'urgentImportant';
        else if (position.x >= midX && position.y < midY) quadrant = 'importantNotUrgent';
        else if (position.x < midX && position.y >= midY) quadrant = 'notUrgentNotImportant';
        else quadrant = 'urgentNotImportant';
      }
      onUpdate(item._id, { x: position.x, y: position.y, quadrant });
    }
  }, [isDragging, position, item._id, item.quadrant, onUpdate, quadrantMode]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const getQuadrantColor = () => {
    if (item.quadrant && QUADRANTS[item.quadrant]) {
      return QUADRANTS[item.quadrant].color;
    }
    return item.priority ? PRIORITY_COLORS[item.priority] : '#FBBF24';
  };

  const color = getQuadrantColor();
  const isCompleted = item.status === 'done' || item.isCompleted;

  // Compact shapes for smaller visual impact
  if (item.shape === 'tag' || item.shape === 'pill') {
    return (
      <div
        className={`
          absolute select-none transition-all duration-150 z-20
          ${isDragging ? 'z-50 cursor-grabbing scale-105' : 'cursor-grab'}
          ${isSelected ? 'ring-2 ring-white/60' : ''}
        `}
        style={{ left: position.x, top: position.y }}
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <div 
          className={`flex items-center gap-2 px-3 py-1.5 ${shape.className} backdrop-blur-sm border transition-colors`}
          style={{ 
            backgroundColor: `${color}25`,
            borderColor: `${color}50`,
            textDecoration: isCompleted ? 'line-through' : 'none',
            opacity: isCompleted ? 0.6 : 1
          }}
        >
          {item.isTodo && (
            <button 
              onClick={(e) => { e.stopPropagation(); onUpdate(item._id, { isCompleted: !isCompleted }); }}
              className="w-4 h-4 rounded border flex items-center justify-center"
              style={{ borderColor: color, backgroundColor: isCompleted ? color : 'transparent' }}
            >
              {isCompleted && <CheckIcon size={10} color="#fff" />}
            </button>
          )}
          <span className="text-xs text-white/90 max-w-[100px] truncate">{item.content}</span>
          {showActions && (
            <button 
              onClick={(e) => { e.stopPropagation(); onDelete(item._id); }}
              className="text-red-400 hover:text-red-300"
            >
              <CloseIcon size={10} color="#EF4444" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Circle shape - minimal
  if (item.shape === 'circle') {
    return (
      <div
        className={`
          absolute select-none transition-all duration-150 z-20
          ${isDragging ? 'z-50 cursor-grabbing scale-105' : 'cursor-grab'}
          ${isSelected ? 'ring-2 ring-blue-400 ring-offset-2 ring-offset-transparent' : ''}
        `}
        style={{ left: position.x, top: position.y }}
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <div 
          className="w-16 h-16 rounded-full flex items-center justify-center border-2 relative group shadow-md backdrop-blur-sm"
          style={{ 
            backgroundColor: 'rgba(255,255,255,0.9)',
            borderColor: color
          }}
        >
          <span className="text-[10px] text-slate-700 text-center px-1 line-clamp-2">{item.content?.substring(0, 20)}</span>
          {showActions && (
            <div className="absolute -top-1 -right-1 flex gap-0.5">
              <button 
                onClick={(e) => { e.stopPropagation(); onDelete(item._id); }}
                className="w-4 h-4 rounded-full bg-red-500/80 flex items-center justify-center"
              >
                <CloseIcon size={8} color="#fff" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Artifact item - compact icon with tooltip
  if (item.type === 'artifact' || item.artifactType) {
    const artifactIcons = {
      link: { icon: '🔗', bg: 'from-blue-500/20 to-blue-600/20', border: '#3B82F6' },
      photo: { icon: '🖼️', bg: 'from-emerald-500/20 to-emerald-600/20', border: '#10B981' },
      website: { icon: '🌐', bg: 'from-purple-500/20 to-purple-600/20', border: '#8B5CF6' },
      note: { icon: '📝', bg: 'from-amber-500/20 to-amber-600/20', border: '#F59E0B' },
      document: { icon: '📄', bg: 'from-slate-500/20 to-slate-600/20', border: '#64748B' },
    };
    const artifactInfo = artifactIcons[item.artifactType] || artifactIcons.link;
    
    return (
      <div
        className={`
          absolute select-none transition-all duration-150 z-20 group
          ${isDragging ? 'z-50 cursor-grabbing scale-110' : 'cursor-grab'}
          ${isSelected ? 'ring-2 ring-blue-400 ring-offset-2' : ''}
        `}
        style={{ left: position.x, top: position.y }}
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
        onClick={() => item.url && window.open(item.url, '_blank')}
      >
        {/* Icon Container */}
        <div 
          className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${artifactInfo.bg} border-2 shadow-lg backdrop-blur-sm hover:scale-105 transition-transform`}
          style={{ borderColor: artifactInfo.border }}
        >
          <span className="text-xl">{artifactInfo.icon}</span>
        </div>
        
        {/* Label Tooltip */}
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-6 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="px-2 py-1 bg-slate-900/90 text-white text-[10px] rounded whitespace-nowrap max-w-[120px] truncate">
            {item.content || item.url}
          </div>
        </div>
        
        {/* Delete button on hover */}
        {showActions && (
          <button 
            onClick={(e) => { e.stopPropagation(); onDelete(item._id); }}
            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center shadow-sm"
          >
            <CloseIcon size={8} color="#fff" />
          </button>
        )}
      </div>
    );
  }

  // Default sticky/card - clean white with colored accent
  return (
    <div
      className={`
        absolute select-none transition-all duration-150 z-20
        ${isDragging ? 'z-50 cursor-grabbing scale-105' : 'cursor-grab'}
        ${isSelected ? 'ring-2 ring-blue-400' : ''}
      `}
      style={{
        left: position.x,
        top: position.y,
        transform: isDragging ? 'rotate(-1deg)' : 'rotate(0deg)',
      }}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div 
        className="min-w-[100px] max-w-[160px] rounded-xl shadow-lg border overflow-hidden backdrop-blur-sm"
        style={{ 
          backgroundColor: 'rgba(255,255,255,0.95)',
          borderColor: 'rgba(148,163,184,0.3)',
        }}
      >
        {/* Minimal header - just colored strip */}
        <div className="h-1.5 rounded-t-sm" style={{ backgroundColor: color }} />
        
        <div className="p-2.5">
          {/* Content with optional todo checkbox */}
          <div className="flex items-start gap-1.5">
            {item.isTodo && (
              <button 
                onClick={(e) => { e.stopPropagation(); onUpdate(item._id, { isCompleted: !isCompleted }); }}
                className="w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center mt-0.5"
                style={{ borderColor: color, backgroundColor: isCompleted ? color : 'transparent' }}
              >
                {isCompleted && <CheckIcon size={8} color="#fff" />}
              </button>
            )}
            {isEditing ? (
              <input
                type="text"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onBlur={() => { onUpdate(item._id, { content: editContent }); setIsEditing(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { onUpdate(item._id, { content: editContent }); setIsEditing(false); }}}
                className="flex-1 text-xs bg-transparent border-none text-slate-700 focus:outline-none"
                autoFocus
              />
            ) : (
              <p 
                className={`flex-1 text-xs text-slate-700 line-clamp-3 ${isCompleted ? 'line-through opacity-60' : ''}`}
                onDoubleClick={() => setIsEditing(true)}
              >
                {item.content}
              </p>
            )}
          </div>
          
          {/* Meta row - due date and assignee */}
          {(item.dueDate || item.assignee) && (
            <div className="flex items-center gap-2 mt-1.5 text-[9px] text-slate-500">
              {item.dueDate && (
                <span className="flex items-center gap-0.5">
                  <CalendarIcon size={8} color="currentColor" />
                  {new Date(item.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              )}
              {item.assignee && (
                <span className="flex items-center gap-0.5">
                  <ProfileIcon size={8} color="currentColor" />
                  {item.assignee}
                </span>
              )}
            </div>
          )}
          
          {/* Actions on hover */}
          {showActions && !isEditing && (
            <div className="flex items-center gap-1 mt-1.5 pt-1.5 border-t border-amber-900/10">
              <button 
                onClick={(e) => { e.stopPropagation(); onUpdate(item._id, { isTodo: !item.isTodo }); }}
                className="p-1 rounded hover:bg-amber-900/10 transition-colors"
                title="Toggle Todo"
              >
                <CheckIcon size={10} color={item.isTodo ? '#10B981' : '#92400e'} />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                className="p-1 rounded hover:bg-amber-900/10 transition-colors"
                title="Edit"
              >
                <SparkleIcon size={10} color="#92400e" />
              </button>
              <div className="flex-1" />
              <button 
                onClick={(e) => { e.stopPropagation(); onDelete(item._id); }}
                className="p-1 rounded hover:bg-red-500/20 transition-colors"
                title="Delete"
              >
                <TrashIcon size={10} color="#EF4444" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// MOBILE-FIRST GRAPH PAPER CANVAS COMPONENT
// Features: 4-quadrant view, double-tap zoom, tap-select-drag, touch-friendly
// ============================================
function GraphPaperCanvas({ 
  items, 
  onUpdateItem, 
  onDeleteItem, 
  selectedItem, 
  onSelectItem,
  onDropMessage,
  teamMembers = [],
  onAddItem,
  drawings = [],
  onDrawingChange,
  isDrawingMode = false,
  setIsDrawingMode,
  // NVC Canvas Overlay props
  showNVCOverlay = false,
  onToggleNVCOverlay,
  activeNVCQuadrant = null,
  onNVCQuadrantClick,
  nvcContentByQuadrant = {}
}) {
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Quadrant focus state for mobile
  const [focusedQuadrant, setFocusedQuadrant] = useState(null); // null = show all 4, or 'urgentImportant', 'importantNotUrgent', etc.
  const [lastTapTime, setLastTapTime] = useState(0);
  const [lastTapQuadrant, setLastTapQuadrant] = useState(null);

  // Touch/drag state for mobile
  const [touchSelectedItem, setTouchSelectedItem] = useState(null);
  const [touchDragStart, setTouchDragStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const canvasRef = useRef(null);
  const drawingCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [drawingColor, setDrawingColor] = useState('#3B82F6');
  const [brushSize, setBrushSize] = useState(2);
  
  // Shape and selection state
  const [drawingTool, setDrawingTool] = useState('freehand'); // 'freehand', 'rectangle', 'circle', 'select'
  const [selectedDrawingId, setSelectedDrawingId] = useState(null);
  const [isDraggingDrawing, setIsDraggingDrawing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [shapeStart, setShapeStart] = useState(null);
  const [currentShape, setCurrentShape] = useState(null);
  
  // Artifact popover state
  const [showArtifactMenu, setShowArtifactMenu] = useState(false);
  const [artifactType, setArtifactType] = useState('link'); // 'link', 'photo', 'note', 'website'
  const [artifactUrl, setArtifactUrl] = useState('');
  const [artifactLabel, setArtifactLabel] = useState('');

  // Quadrant definitions for mobile view with custom icons
  const quadrantDefs = {
    urgentImportant: { label: 'Do First', Icon: FireIcon, color: '#EF4444', bgColor: 'rgba(239,68,68,0.08)', position: 'top-left' },
    importantNotUrgent: { label: 'Schedule', Icon: CalendarIcon, color: '#F59E0B', bgColor: 'rgba(245,158,11,0.08)', position: 'top-right' },
    urgentNotImportant: { label: 'Delegate', Icon: UsersIcon, color: '#3B82F6', bgColor: 'rgba(59,130,246,0.08)', position: 'bottom-left' },
    notUrgentNotImportant: { label: 'Eliminate', Icon: BacklogIcon, color: '#6B7280', bgColor: 'rgba(107,114,128,0.08)', position: 'bottom-right' },
  };

  // Handle double-tap on quadrant (mobile zoom) - more reliable
  const handleQuadrantTap = (quadrantKey, e) => {
    // Don't trigger if tapping on an item
    if (e && e.target.closest('[data-item]')) return;
    
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 400; // Increased for reliability
    
    if (lastTapQuadrant === quadrantKey && now - lastTapTime < DOUBLE_TAP_DELAY) {
      // Double tap detected
      if (focusedQuadrant === quadrantKey) {
        // Already focused - zoom back out
        setFocusedQuadrant(null);
        toast.success('Zoomed out to all quadrants');
      } else {
        // Zoom into this quadrant
        setFocusedQuadrant(quadrantKey);
        toast.success(`Focused on ${quadrantDefs[quadrantKey].label}`);
      }
      setLastTapTime(0);
      setLastTapQuadrant(null);
    } else {
      setLastTapTime(now);
      setLastTapQuadrant(quadrantKey);
    }
  };

  // Touch event handlers for item dragging
  const handleItemTouchStart = (e, item) => {
    e.stopPropagation();
    const touch = e.touches[0];
    setTouchSelectedItem(item._id);
    onSelectItem(item);
    setTouchDragStart({ x: touch.clientX, y: touch.clientY, itemX: item.x || 0, itemY: item.y || 0 });
  };

  const handleItemTouchMove = (e) => {
    if (!touchSelectedItem || !touchDragStart) return;
    e.preventDefault();
    const touch = e.touches[0];
    const dx = touch.clientX - touchDragStart.x;
    const dy = touch.clientY - touchDragStart.y;
    
    // Update item position
    const newX = Math.max(0, touchDragStart.itemX + dx);
    const newY = Math.max(0, touchDragStart.itemY + dy);
    onUpdateItem(touchSelectedItem, { x: newX, y: newY });
    setIsDragging(true);
  };

  const handleItemTouchEnd = () => {
    if (!isDragging && touchSelectedItem) {
      // Just a tap, not a drag - keep selected
    }
    setTouchDragStart(null);
    setIsDragging(false);
    // Don't clear touchSelectedItem - keep it selected for potential drag
  };

  // Get items for a specific quadrant - map priority to quadrant (exclusive)
  // Supports both string priorities ("high", "medium", "low", "none") and numeric (1, 2, 3, 4)
  const getQuadrantItems = (quadrantKey) => {
    // Map string priorities to numeric for consistent handling
    const stringToNumeric = {
      'high': 1, 'urgent': 1,
      'medium': 2, 'important': 2,
      'low': 3,
      'none': 4, 'consider': 4
    };
    
    const priorityMap = {
      'urgentImportant': [1],        // P1 - Do First (high priority)
      'importantNotUrgent': [2],     // P2 - Schedule (medium priority)
      'urgentNotImportant': [3],     // P3 - Delegate (low priority)
      'notUrgentNotImportant': [4, 5, 6], // P4-P6 - Eliminate/Consider (none/default)
    };
    const priorities = priorityMap[quadrantKey] || [];
    return items.filter(item => {
      // Handle both string and numeric priorities
      let itemPriority = item.priority;
      if (typeof itemPriority === 'string') {
        itemPriority = stringToNumeric[itemPriority.toLowerCase()] || 4;
      }
      itemPriority = itemPriority || 4;
      return priorities.includes(itemPriority);
    });
  };

  // Handle double-tap on quadrant background (for zooming)
  const handleQuadrantBackgroundTap = (e, quadrantKey) => {
    // Only handle taps on the background, not on items
    if (e.target !== e.currentTarget && !e.target.classList.contains('quadrant-bg')) return;
    handleQuadrantTap(quadrantKey);
  };

  // Drawing functions
  const startDrawing = (e) => {
    if (!isDrawingMode) return;
    const rect = drawingCanvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (drawingTool === 'select') {
      // Check if clicking on an existing drawing
      const clickedDrawing = findDrawingAtPoint(x, y);
      if (clickedDrawing) {
        setSelectedDrawingId(clickedDrawing.id);
        setIsDraggingDrawing(true);
        setDragStart({ x, y });
      } else {
        setSelectedDrawingId(null);
      }
      return;
    }
    
    if (drawingTool === 'freehand') {
      setIsDrawing(true);
      setCurrentPath([{ x, y }]);
    } else if (drawingTool === 'rectangle' || drawingTool === 'circle') {
      setShapeStart({ x, y });
      setCurrentShape({ type: drawingTool, x, y, width: 0, height: 0 });
    }
  };

  const draw = (e) => {
    if (!isDrawingMode) return;
    const rect = drawingCanvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (isDraggingDrawing && selectedDrawingId) {
      // Move the selected drawing
      const dx = x - dragStart.x;
      const dy = y - dragStart.y;
      moveDrawing(selectedDrawingId, dx, dy);
      setDragStart({ x, y });
      return;
    }
    
    if (drawingTool === 'freehand' && isDrawing) {
      setCurrentPath(prev => [...prev, { x, y }]);
    } else if ((drawingTool === 'rectangle' || drawingTool === 'circle') && shapeStart) {
      setCurrentShape({
        type: drawingTool,
        x: Math.min(shapeStart.x, x),
        y: Math.min(shapeStart.y, y),
        width: Math.abs(x - shapeStart.x),
        height: Math.abs(y - shapeStart.y)
      });
    }
  };

  const endDrawing = () => {
    if (isDraggingDrawing) {
      setIsDraggingDrawing(false);
      return;
    }
    
    if (drawingTool === 'freehand' && isDrawing && currentPath.length > 1) {
      const newDrawing = {
        id: Date.now().toString(),
        type: 'freehand',
        path: currentPath,
        color: drawingColor,
        brushSize: brushSize,
        offsetX: 0,
        offsetY: 0
      };
      onDrawingChange && onDrawingChange([...drawings, newDrawing]);
    } else if ((drawingTool === 'rectangle' || drawingTool === 'circle') && currentShape && currentShape.width > 5) {
      const newDrawing = {
        id: Date.now().toString(),
        type: drawingTool,
        x: currentShape.x,
        y: currentShape.y,
        width: currentShape.width,
        height: currentShape.height,
        color: drawingColor,
        brushSize: brushSize,
        offsetX: 0,
        offsetY: 0
      };
      onDrawingChange && onDrawingChange([...drawings, newDrawing]);
    }
    
    setIsDrawing(false);
    setCurrentPath([]);
    setShapeStart(null);
    setCurrentShape(null);
  };

  // Find drawing at a point (for selection)
  const findDrawingAtPoint = (x, y) => {
    for (let i = drawings.length - 1; i >= 0; i--) {
      const d = drawings[i];
      const ox = d.offsetX || 0;
      const oy = d.offsetY || 0;
      
      if (d.type === 'freehand' && d.path) {
        // Check if point is near any point in the path
        for (const p of d.path) {
          if (Math.abs((p.x + ox) - x) < 15 && Math.abs((p.y + oy) - y) < 15) {
            return d;
          }
        }
      } else if (d.type === 'rectangle') {
        if (x >= (d.x + ox) && x <= (d.x + ox + d.width) && 
            y >= (d.y + oy) && y <= (d.y + oy + d.height)) {
          return d;
        }
      } else if (d.type === 'circle') {
        const cx = (d.x + ox) + d.width / 2;
        const cy = (d.y + oy) + d.height / 2;
        const rx = d.width / 2;
        const ry = d.height / 2;
        const dist = Math.pow((x - cx) / rx, 2) + Math.pow((y - cy) / ry, 2);
        if (dist <= 1) return d;
      }
    }
    return null;
  };

  // Move a drawing by offset
  const moveDrawing = (id, dx, dy) => {
    const updated = drawings.map(d => {
      if (d.id === id) {
        return { ...d, offsetX: (d.offsetX || 0) + dx, offsetY: (d.offsetY || 0) + dy };
      }
      return d;
    });
    onDrawingChange && onDrawingChange(updated);
  };

  // Delete selected drawing
  const deleteSelectedDrawing = () => {
    if (selectedDrawingId) {
      const updated = drawings.filter(d => d.id !== selectedDrawingId);
      onDrawingChange && onDrawingChange(updated);
      setSelectedDrawingId(null);
    }
  };

  // Add artifact to canvas
  const addArtifact = () => {
    if (!artifactLabel && !artifactUrl) return;
    
    // Get random position on canvas
    const x = 100 + Math.random() * 300;
    const y = 100 + Math.random() * 200;
    
    onAddItem && onAddItem({
      content: artifactLabel || artifactUrl,
      url: artifactUrl,
      type: 'artifact',
      artifactType: artifactType,
      x, y,
      shape: artifactType === 'photo' ? 'photo' : artifactType === 'website' ? 'website' : artifactType === 'note' ? 'sticky' : 'link',
      quadrant: 'urgentImportant'
    });
    
    setArtifactUrl('');
    setArtifactLabel('');
    setShowArtifactMenu(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const messageData = e.dataTransfer.getData('message');
    const textData = e.dataTransfer.getData('text/plain');
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const midX = rect.width / 2;
    const midY = rect.height / 2;
    let quadrant = 'notUrgentNotImportant';
    
    if (x < midX && y < midY) quadrant = 'urgentImportant';
    else if (x >= midX && y < midY) quadrant = 'importantNotUrgent';
    else if (x < midX && y >= midY) quadrant = 'notUrgentNotImportant';
    else quadrant = 'urgentNotImportant';
    
    if (messageData) {
      const message = JSON.parse(messageData);
      onDropMessage(message, x, y, quadrant);
    } else if (textData) {
      // Handle quick thoughts dropped on canvas
      onAddItem && onAddItem({
        content: textData,
        type: 'note',
        x, y, quadrant,
        shape: 'sticky'
      });
    }
  };

  // Render SVG path from points
  const renderPath = (points, offsetX = 0, offsetY = 0) => {
    if (points.length < 2) return '';
    let d = `M ${points[0].x + offsetX} ${points[0].y + offsetY}`;
    for (let i = 1; i < points.length; i++) {
      d += ` L ${points[i].x + offsetX} ${points[i].y + offsetY}`;
    }
    return d;
  };

  // Mobile-first quadrant view renderer
  const renderMobileQuadrantView = () => {
    const quadrantKeys = ['urgentImportant', 'importantNotUrgent', 'urgentNotImportant', 'notUrgentNotImportant'];
    
    if (focusedQuadrant) {
      // Focused on single quadrant
      const q = quadrantDefs[focusedQuadrant];
      const qItems = getQuadrantItems(focusedQuadrant);
      
      return (
        <div 
          className="absolute inset-0 flex flex-col"
          style={{ backgroundColor: q.bgColor }}
          onTouchMove={handleItemTouchMove}
          onTouchEnd={handleItemTouchEnd}
        >
          {/* Header with back button */}
          <div 
            className="flex items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: `${q.color}30` }}
          >
            <div className="flex items-center gap-3">
              <q.Icon size={28} color={q.color} />
              <div>
                <h2 className="text-lg font-semibold" style={{ color: q.color }}>{q.label}</h2>
                <p className="text-xs text-slate-500">{qItems.length} items</p>
              </div>
            </div>
            <button
              onClick={() => setFocusedQuadrant(null)}
              className="px-4 py-2 rounded-xl bg-white/80 backdrop-blur border border-slate-200 text-sm font-medium text-slate-600 active:scale-95 transition-transform"
            >
              ← Back to All
            </button>
          </div>
          
          {/* Items in focused quadrant */}
          <div className="flex-1 overflow-auto p-4">
            {qItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <q.Icon size={48} color={q.color} />
                <p className="text-slate-400 text-sm mt-3">No items yet</p>
                <p className="text-slate-300 text-xs mt-1">Double-tap to go back</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {qItems.map(item => (
                  <div
                    key={item._id}
                    className={`p-4 rounded-2xl bg-white border-2 shadow-sm active:scale-[0.98] transition-transform ${
                      touchSelectedItem === item._id ? 'ring-2 ring-offset-2' : ''
                    }`}
                    style={{ 
                      borderColor: touchSelectedItem === item._id ? q.color : `${q.color}40`,
                      ringColor: q.color
                    }}
                    onTouchStart={(e) => handleItemTouchStart(e, item)}
                    onClick={() => onSelectItem(item)}
                  >
                    <p className="text-slate-800 font-medium">{item.content}</p>
                    {item.dueDate && (
                      <p className="text-xs text-slate-400 mt-2">Due: {new Date(item.dueDate).toLocaleDateString()}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Hint at bottom */}
          <div className="px-4 py-2 bg-white/50 border-t border-slate-200/50 text-center">
            <p className="text-xs text-slate-400">Double-tap anywhere to zoom out</p>
          </div>
        </div>
      );
    }
    
    // Show all 4 quadrants in 2x2 grid
    return (
      <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-1 p-1 bg-white/50">
        {quadrantKeys.map((qKey) => {
          const q = quadrantDefs[qKey];
          const qItems = getQuadrantItems(qKey);
          
          return (
            <div
              key={qKey}
              className="quadrant-bg relative rounded-xl overflow-hidden active:scale-[0.98] transition-transform touch-manipulation cursor-pointer"
              style={{ backgroundColor: q.bgColor }}
              onClick={(e) => handleQuadrantTap(qKey, e)}
              onTouchMove={handleItemTouchMove}
              onTouchEnd={handleItemTouchEnd}
            >
              {/* Quadrant header */}
              <div className="absolute top-0 left-0 right-0 px-3 py-2 bg-gradient-to-b from-white/80 to-transparent pointer-events-none z-10">
                <div className="flex items-center gap-2">
                  <q.Icon size={20} color={q.color} />
                  <span className="text-sm font-semibold" style={{ color: q.color }}>{q.label}</span>
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-white/60" style={{ color: q.color }}>
                    {qItems.length}
                  </span>
                </div>
              </div>
              
              {/* Items preview */}
              <div className="absolute inset-0 pt-10 px-2 pb-2 overflow-hidden pointer-events-none">
                {qItems.slice(0, 3).map(item => (
                  <div
                    key={item._id}
                    data-item="true"
                    className={`mb-1.5 p-2 rounded-lg bg-white/90 border shadow-sm text-xs truncate pointer-events-auto ${
                      touchSelectedItem === item._id ? 'ring-2' : ''
                    }`}
                    style={{ 
                      borderColor: `${q.color}30`,
                      ringColor: q.color
                    }}
                    onTouchStart={(e) => { e.stopPropagation(); handleItemTouchStart(e, item); }}
                    onClick={(e) => { e.stopPropagation(); onSelectItem(item); }}
                  >
                    {item.content}
                  </div>
                ))}
                {qItems.length > 3 && (
                  <p className="text-xs text-center mt-1" style={{ color: q.color }}>
                    +{qItems.length - 3} more
                  </p>
                )}
                {qItems.length === 0 && (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-xs text-slate-300">Double-tap to expand</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        
        {/* Center hint */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <div className="w-10 h-10 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center border border-slate-200">
            <span className="text-lg">📋</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div 
      ref={canvasRef}
      className="relative w-full h-full overflow-hidden rounded-xl"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{
        // Clean white graph paper background
        backgroundColor: '#FAFBFC',
        backgroundImage: isMobile ? 'none' : `
          linear-gradient(rgba(147,197,253,0.3) 1px, transparent 1px),
          linear-gradient(90deg, rgba(147,197,253,0.3) 1px, transparent 1px),
          linear-gradient(rgba(147,197,253,0.15) 1px, transparent 1px),
          linear-gradient(90deg, rgba(147,197,253,0.15) 1px, transparent 1px)
        `,
        backgroundSize: '100px 100px, 100px 100px, 20px 20px, 20px 20px',
        backgroundPosition: '-1px -1px, -1px -1px, -1px -1px, -1px -1px',
        boxShadow: isMobile ? 'none' : 'inset 0 0 60px rgba(147,197,253,0.08)'
      }}
    >
      {/* Mobile View - 4 Quadrant Grid with Double-Tap Zoom */}
      {isMobile && renderMobileQuadrantView()}

      {/* Desktop View - Original Canvas */}
      {!isMobile && (
        <>
      {/* Drawing Tools Bar - frosted glass */}
      <div className="absolute top-2 left-2 z-40 flex items-center gap-2 bg-white/80 backdrop-blur-md rounded-lg px-3 py-2 border border-slate-200/60 shadow-lg">
        {/* Tool Selection */}
        <button
          onClick={() => { setIsDrawingMode && setIsDrawingMode(!isDrawingMode); setDrawingTool('freehand'); }}
          className={`p-1.5 rounded transition-colors ${isDrawingMode && drawingTool === 'freehand' ? 'bg-blue-500/20 text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
          title="Freehand Draw"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 19l7-7 3 3-7 7-3-3z"/>
            <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
            <path d="M2 2l7.586 7.586"/>
            <circle cx="11" cy="11" r="2"/>
          </svg>
        </button>
        
        {/* Rectangle Tool */}
        <button
          onClick={() => { setIsDrawingMode && setIsDrawingMode(true); setDrawingTool('rectangle'); }}
          className={`p-1.5 rounded transition-colors ${isDrawingMode && drawingTool === 'rectangle' ? 'bg-blue-500/20 text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
          title="Rectangle"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
          </svg>
        </button>
        
        {/* Circle Tool */}
        <button
          onClick={() => { setIsDrawingMode && setIsDrawingMode(true); setDrawingTool('circle'); }}
          className={`p-1.5 rounded transition-colors ${isDrawingMode && drawingTool === 'circle' ? 'bg-blue-500/20 text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
          title="Circle/Ellipse"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
          </svg>
        </button>
        
        {/* Select Tool */}
        <button
          onClick={() => { setIsDrawingMode && setIsDrawingMode(true); setDrawingTool('select'); }}
          className={`p-1.5 rounded transition-colors ${isDrawingMode && drawingTool === 'select' ? 'bg-blue-500/20 text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
          title="Select & Move"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"/>
          </svg>
        </button>
        
        {isDrawingMode && (
          <>
            <div className="w-px h-5 bg-slate-300" />
            {/* Colors */}
            <div className="flex gap-1">
              {['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#1F2937'].map(color => (
                <button
                  key={color}
                  onClick={() => setDrawingColor(color)}
                  className={`w-5 h-5 rounded-full border-2 transition-transform ${drawingColor === color ? 'scale-110 border-blue-400' : 'border-slate-200'}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <div className="w-px h-5 bg-slate-300" />
            {/* Brush Size */}
            <input
              type="range"
              min="1"
              max="8"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
              className="w-16 accent-blue-500"
            />
            {/* Delete selected */}
            {selectedDrawingId && (
              <>
                <div className="w-px h-5 bg-slate-300" />
                <button
                  onClick={deleteSelectedDrawing}
                  className="p-1.5 rounded text-red-500 hover:bg-red-50 transition-colors"
                  title="Delete Selected"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                  </svg>
                </button>
              </>
            )}
          </>
        )}
        
        {/* NVC Overlay Toggle */}
        <div className="w-px h-5 bg-slate-300" />
        <NVCOverlayToggle 
          isActive={showNVCOverlay}
          onToggle={() => onToggleNVCOverlay && onToggleNVCOverlay()}
          className="text-xs"
        />
        
        {/* Artifact Add Menu */}
        <div className="w-px h-5 bg-slate-300" />
        <div className="relative">
          <button
            onClick={() => setShowArtifactMenu(!showArtifactMenu)}
            className={`p-1.5 rounded transition-colors ${showArtifactMenu ? 'bg-emerald-500/20 text-emerald-600' : 'text-slate-500 hover:text-slate-700'}`}
            title="Add Artifact"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>
          
          {showArtifactMenu && (
            <div className="absolute top-full left-0 mt-2 w-72 bg-white/95 backdrop-blur-lg rounded-xl shadow-xl border border-slate-200/60 p-3 z-50">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Add Artifact</h4>
              
              {/* Type Selection */}
              <div className="flex gap-1 mb-3">
                {[
                  { type: 'link', icon: '🔗', label: 'Link' },
                  { type: 'photo', icon: '🖼️', label: 'Photo' },
                  { type: 'website', icon: '🌐', label: 'Website' },
                  { type: 'note', icon: '📝', label: 'Note' },
                ].map(({ type, icon, label }) => (
                  <button
                    key={type}
                    onClick={() => setArtifactType(type)}
                    className={`flex-1 py-1.5 text-xs rounded-lg transition-colors ${
                      artifactType === type 
                        ? 'bg-emerald-500/20 text-emerald-700 border border-emerald-300' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <span className="text-sm">{icon}</span>
                    <span className="ml-1">{label}</span>
                  </button>
                ))}
              </div>
              
              {/* URL Input (for link, photo, website) */}
              {artifactType !== 'note' && (
                <input
                  type="url"
                  value={artifactUrl}
                  onChange={(e) => setArtifactUrl(e.target.value)}
                  placeholder={artifactType === 'photo' ? 'Image URL...' : 'URL...'}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white mb-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              )}
              
              {/* Label Input */}
              <input
                type="text"
                value={artifactLabel}
                onChange={(e) => setArtifactLabel(e.target.value)}
                placeholder={artifactType === 'note' ? 'Note content...' : 'Label (optional)...'}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white mb-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              />
              
              <div className="flex gap-2">
                <button
                  onClick={addArtifact}
                  className="flex-1 py-2 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-colors"
                >
                  Add to Canvas
                </button>
                <button
                  onClick={() => setShowArtifactMenu(false)}
                  className="px-3 py-2 bg-slate-100 text-slate-600 text-sm rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quadrant Dividers - subtle blue */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-blue-300/40" style={{ boxShadow: '1px 0 2px rgba(147,197,253,0.2)' }} />
        <div className="absolute top-1/2 left-0 right-0 h-px bg-blue-300/40" style={{ boxShadow: '0 1px 2px rgba(147,197,253,0.2)' }} />
      </div>

      {/* Quadrant Labels - clean style */}
      <div className="absolute top-14 left-3 text-xs font-medium text-slate-400" style={{ fontFamily: "'Figtree', sans-serif" }}>Do First</div>
      <div className="absolute top-14 right-3 text-xs font-medium text-slate-400 text-right" style={{ fontFamily: "'Figtree', sans-serif" }}>Schedule</div>
      <div className="absolute bottom-3 left-3 text-xs font-medium text-slate-400" style={{ fontFamily: "'Figtree', sans-serif" }}>Eliminate</div>
      <div className="absolute bottom-3 right-3 text-xs font-medium text-slate-400 text-right" style={{ fontFamily: "'Figtree', sans-serif" }}>Delegate</div>

      {/* Drawing Layer */}
      <svg 
        ref={drawingCanvasRef}
        className={`absolute inset-0 w-full h-full ${isDrawingMode ? (drawingTool === 'select' ? 'cursor-pointer' : 'cursor-crosshair') + ' z-30' : 'pointer-events-none z-10'}`}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={endDrawing}
        onMouseLeave={endDrawing}
      >
        {/* Saved drawings */}
        {drawings.map(drawing => {
          const isSelected = drawing.id === selectedDrawingId;
          const ox = drawing.offsetX || 0;
          const oy = drawing.offsetY || 0;
          
          if (drawing.type === 'rectangle') {
            return (
              <g key={drawing.id}>
                {isSelected && (
                  <rect
                    x={drawing.x + ox - 3}
                    y={drawing.y + oy - 3}
                    width={drawing.width + 6}
                    height={drawing.height + 6}
                    stroke="#3B82F6"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    fill="none"
                  />
                )}
                <rect
                  x={drawing.x + ox}
                  y={drawing.y + oy}
                  width={drawing.width}
                  height={drawing.height}
                  stroke={drawing.color}
                  strokeWidth={drawing.brushSize}
                  fill="none"
                  opacity="0.8"
                  rx="4"
                />
              </g>
            );
          } else if (drawing.type === 'circle') {
            return (
              <g key={drawing.id}>
                {isSelected && (
                  <ellipse
                    cx={(drawing.x + ox) + drawing.width / 2}
                    cy={(drawing.y + oy) + drawing.height / 2}
                    rx={drawing.width / 2 + 3}
                    ry={drawing.height / 2 + 3}
                    stroke="#3B82F6"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    fill="none"
                  />
                )}
                <ellipse
                  cx={(drawing.x + ox) + drawing.width / 2}
                  cy={(drawing.y + oy) + drawing.height / 2}
                  rx={drawing.width / 2}
                  ry={drawing.height / 2}
                  stroke={drawing.color}
                  strokeWidth={drawing.brushSize}
                  fill="none"
                  opacity="0.8"
                />
              </g>
            );
          } else {
            // Freehand path
            return (
              <g key={drawing.id}>
                {isSelected && (
                  <path
                    d={renderPath(drawing.path, ox, oy)}
                    stroke="#3B82F6"
                    strokeWidth={drawing.brushSize + 6}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity="0.3"
                  />
                )}
                <path
                  d={renderPath(drawing.path, ox, oy)}
                  stroke={drawing.color}
                  strokeWidth={drawing.brushSize}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.7"
                />
              </g>
            );
          }
        })}
        {/* Current drawing preview */}
        {currentPath.length > 1 && (
          <path
            d={renderPath(currentPath)}
            stroke={drawingColor}
            strokeWidth={brushSize}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {/* Shape preview */}
        {currentShape && (
          currentShape.type === 'rectangle' ? (
            <rect
              x={currentShape.x}
              y={currentShape.y}
              width={currentShape.width}
              height={currentShape.height}
              stroke={drawingColor}
              strokeWidth={brushSize}
              fill="none"
              strokeDasharray="5,5"
              rx="4"
            />
          ) : (
            <ellipse
              cx={currentShape.x + currentShape.width / 2}
              cy={currentShape.y + currentShape.height / 2}
              rx={currentShape.width / 2}
              ry={currentShape.height / 2}
              stroke={drawingColor}
              strokeWidth={brushSize}
              fill="none"
              strokeDasharray="5,5"
            />
          )
        )}
      </svg>

      {/* NVC Canvas Overlay - OFNR Quadrants */}
      <NVCCanvasOverlay
        isVisible={showNVCOverlay}
        activeQuadrant={activeNVCQuadrant}
        onQuadrantClick={onNVCQuadrantClick}
        contentByQuadrant={nvcContentByQuadrant}
      />

      {/* Canvas Items */}
      {items.map((item) => (
        <EnhancedCanvasItem
          key={item._id}
          item={item}
          onUpdate={onUpdateItem}
          onDelete={onDeleteItem}
          isSelected={selectedItem === item._id}
          onSelect={onSelectItem}
          quadrantMode={true}
        />
      ))}
        </>
      )}
    </div>
  );
}

// ============================================
// BOARD VIEW - Now/Next/Someday/Done Columns
// ============================================

function BoardView({ 
  items, 
  onUpdateItem, 
  onDeleteItem, 
  onAddItem,
  teamMembers = []
}) {
  const [newItemColumn, setNewItemColumn] = useState(null);
  const [newItemContent, setNewItemContent] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);

  const getItemsByStatus = (status) => {
    return items.filter(item => item.status === status);
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    if (draggedItem && draggedItem.status !== newStatus) {
      onUpdateItem(draggedItem._id, { status: newStatus });
    }
    setDraggedItem(null);
  };

  const handleAddItem = (column) => {
    if (!newItemContent.trim()) return;
    onAddItem({
      content: newItemContent,
      type: 'todo',
      status: column,
      isTodo: true,
      shape: 'card'
    });
    setNewItemContent('');
    setNewItemColumn(null);
  };

  return (
    <div className="h-full p-4" style={{
      backgroundColor: '#1a1a2e',
      backgroundImage: `
        linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
      `,
      backgroundSize: '20px 20px'
    }}>
      <div className="grid grid-cols-4 gap-4 h-full">
        {Object.entries(BOARD_COLUMNS).map(([status, config]) => (
          <div
            key={status}
            className="flex flex-col rounded-xl overflow-hidden"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, status)}
          >
            {/* Column Header */}
            <div 
              className="px-4 py-3 flex items-center justify-between"
              style={{ backgroundColor: `${config.color}15`, borderBottom: `2px solid ${config.color}40` }}
            >
              <div>
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }} />
                  {config.label}
                </h3>
                <p className="text-[10px] text-white/40">{config.description}</p>
              </div>
              <Badge className="text-xs" style={{ backgroundColor: `${config.color}30`, color: config.color }}>
                {getItemsByStatus(status).length}
              </Badge>
            </div>

            {/* Column Content */}
            <div className="flex-1 p-2 space-y-2 overflow-y-auto bg-black/20">
              {getItemsByStatus(status).map(item => (
                <div
                  key={item._id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item)}
                  className={`
                    p-3 rounded-lg bg-white/5 border border-white/10 
                    cursor-grab active:cursor-grabbing
                    hover:bg-white/10 hover:border-white/20 transition-all
                    ${draggedItem?._id === item._id ? 'opacity-50' : ''}
                  `}
                >
                  <div className="flex items-start gap-2">
                    {item.isTodo && (
                      <button
                        onClick={() => onUpdateItem(item._id, { 
                          isCompleted: !item.isCompleted,
                          status: !item.isCompleted ? 'done' : item.status
                        })}
                        className="w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ 
                          borderColor: config.color, 
                          backgroundColor: item.isCompleted ? config.color : 'transparent' 
                        }}
                      >
                        {item.isCompleted && <CheckIcon size={10} color="#fff" />}
                      </button>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm text-white/90 ${item.isCompleted ? 'line-through opacity-60' : ''}`}>
                        {item.content}
                      </p>
                      {(item.dueDate || item.assignee) && (
                        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-white/40">
                          {item.dueDate && (
                            <span className="flex items-center gap-1">
                              <CalendarIcon size={10} color="currentColor" />
                              {new Date(item.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                          {item.assignee && (
                            <span className="flex items-center gap-1">
                              <ProfileIcon size={10} color="currentColor" />
                              {item.assignee}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => onDeleteItem(item._id)}
                      className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded transition-all"
                    >
                      <TrashIcon size={12} color="#EF4444" />
                    </button>
                  </div>
                </div>
              ))}

              {/* Add Item */}
              {newItemColumn === status ? (
                <div className="p-2 rounded-lg bg-white/5 border border-white/20">
                  <Input
                    value={newItemContent}
                    onChange={(e) => setNewItemContent(e.target.value)}
                    placeholder="What needs to be done?"
                    className="bg-transparent border-0 text-white text-sm placeholder:text-white/30 p-0 h-auto focus-visible:ring-0"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddItem(status);
                      if (e.key === 'Escape') { setNewItemColumn(null); setNewItemContent(''); }
                    }}
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <Button 
                      size="sm" 
                      onClick={() => handleAddItem(status)}
                      className="h-7 text-xs"
                      style={{ backgroundColor: config.color }}
                    >
                      Add
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => { setNewItemColumn(null); setNewItemContent(''); }}
                      className="h-7 text-xs text-white/50"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setNewItemColumn(status)}
                  className="w-full p-2 rounded-lg border border-dashed border-white/10 text-white/30 text-sm hover:border-white/20 hover:text-white/50 transition-colors flex items-center justify-center gap-1"
                >
                  <PlusIcon size={14} color="currentColor" />
                  Add item
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// ENHANCED TODO LIST - Minimal, Clean Design
// Features: Today view, Eisenhower Matrix, Recurring tasks, Due dates
// ============================================

// Recurring options
const RECURRENCE_OPTIONS = [
  { value: null, label: 'No repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
];

// Helper to check if date is today
const isToday = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

// Helper to check if date is this week
const isThisWeek = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const today = new Date();
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay());
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);
  return date >= weekStart && date < weekEnd;
};

// Helper to check if date is overdue
const isOverdue = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

// Format relative date
const formatRelativeDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  if (isOverdue(dateStr)) return `Overdue (${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function TodoListView({ 
  items, 
  onUpdateItem, 
  onDeleteItem, 
  onAddItem,
  messages = []
}) {
  const [newTodo, setNewTodo] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [activeView, setActiveView] = useState('today'); // 'today', 'week', 'matrix', 'backlog', 'all'
  const [expandedTodo, setExpandedTodo] = useState(null);
  const [newTodoDueDate, setNewTodoDueDate] = useState('');
  const [newTodoRecurrence, setNewTodoRecurrence] = useState(null);
  const fileInputRef = React.useRef({});

  // Handle image upload for a todo
  const handleImageUpload = (todoId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      onUpdateItem(todoId, { image: reader.result });
      toast.success('Image added!');
    };
    reader.readAsDataURL(file);
  };

  // Get all todos - handle both camelCase (frontend) and snake_case (backend) field names
  const getAllTodos = () => {
    const itemTodos = items.filter(item => item.isTodo || item.is_todo || item.type === 'todo');
    const messageTodos = messages.filter(msg => msg.isTodo || msg.is_todo);
    return [...itemTodos, ...messageTodos].map(todo => ({
      ...todo,
      priority: todo.priority || 4,
      // Normalize field names
      isCompleted: todo.isCompleted || todo.todo_completed || false,
      isTodo: true
    }));
  };

  const allTodos = getAllTodos();

  // Filter todos based on view
  const getFilteredTodos = () => {
    let filtered = showCompleted ? allTodos : allTodos.filter(t => !t.isCompleted);
    
    switch (activeView) {
      case 'today':
        // Today: Due today OR overdue OR no due date with priority 1-2
        return filtered.filter(t => 
          isToday(t.dueDate) || 
          isOverdue(t.dueDate) ||
          (!t.dueDate && t.priority <= 2)
        ).sort((a, b) => {
          // Overdue first, then by priority
          if (isOverdue(a.dueDate) && !isOverdue(b.dueDate)) return -1;
          if (!isOverdue(a.dueDate) && isOverdue(b.dueDate)) return 1;
          return (a.priority || 4) - (b.priority || 4);
        });
      
      case 'week':
        // This week: Has due date within this week
        return filtered.filter(t => isThisWeek(t.dueDate))
          .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
      
      case 'matrix':
        // All with priorities, grouped by Eisenhower quadrant
        return filtered.sort((a, b) => (a.priority || 4) - (b.priority || 4));
      
      case 'backlog':
        // Priority 5-6 OR no due date with priority 3-4
        return filtered.filter(t => 
          t.priority >= 5 || 
          (!t.dueDate && t.priority >= 3)
        ).sort((a, b) => (a.priority || 4) - (b.priority || 4));
      
      default:
        return filtered.sort((a, b) => (a.priority || 4) - (b.priority || 4));
    }
  };

  const filteredTodos = getFilteredTodos();

  // Handle recurring task completion
  const handleComplete = (todo) => {
    if (todo.recurrence && !todo.isCompleted) {
      // Create next occurrence
      const nextDate = new Date(todo.dueDate || new Date());
      switch (todo.recurrence) {
        case 'daily': nextDate.setDate(nextDate.getDate() + 1); break;
        case 'weekly': nextDate.setDate(nextDate.getDate() + 7); break;
        case 'biweekly': nextDate.setDate(nextDate.getDate() + 14); break;
        case 'monthly': nextDate.setMonth(nextDate.getMonth() + 1); break;
        default: break;
      }
      
      // Create new todo for next occurrence
      onAddItem({
        content: todo.content,
        type: 'todo',
        isTodo: true,
        is_todo: true,
        isCompleted: false,
        todo_completed: false,
        priority: todo.priority,
        dueDate: nextDate.toISOString().split('T')[0],
        recurrence: todo.recurrence,
        status: 'now',
        shape: 'card'
      });
      
      // Mark current as completed (non-recurring)
      onUpdateItem(todo._id, { isCompleted: true, todo_completed: true, recurrence: null });
      toast.success('Completed! Next occurrence created.');
    } else {
      const newCompleted = !todo.isCompleted;
      onUpdateItem(todo._id, { isCompleted: newCompleted, todo_completed: newCompleted });
    }
  };

  const handleAddTodo = () => {
    if (!newTodo.trim()) return;
    onAddItem({
      content: newTodo,
      type: 'todo',
      isTodo: true,
      is_todo: true,
      isCompleted: false,
      todo_completed: false,
      priority: 4,
      dueDate: newTodoDueDate || null,
      recurrence: newTodoRecurrence,
      status: 'now',
      shape: 'card'
    });
    setNewTodo('');
    setNewTodoDueDate('');
    setNewTodoRecurrence(null);
  };

  const getPriorityStyle = (priority) => {
    const styles = {
      1: { bg: 'bg-red-500/8', border: 'border-red-500/20', text: 'text-red-400', dot: 'bg-red-500', label: 'Urgent & Important' },
      2: { bg: 'bg-amber-500/8', border: 'border-amber-500/20', text: 'text-amber-400', dot: 'bg-amber-500', label: 'Important' },
      3: { bg: 'bg-blue-500/8', border: 'border-blue-500/20', text: 'text-blue-400', dot: 'bg-blue-500', label: 'Scheduled' },
      4: { bg: 'bg-slate-500/8', border: 'border-slate-500/20', text: 'text-slate-400', dot: 'bg-slate-500', label: 'Normal' },
      5: { bg: 'bg-purple-500/8', border: 'border-purple-500/20', text: 'text-purple-400', dot: 'bg-purple-500', label: 'Backlog' },
      6: { bg: 'bg-gray-500/8', border: 'border-gray-500/20', text: 'text-gray-500', dot: 'bg-gray-600', label: 'Someday' }
    };
    return styles[priority] || styles[4];
  };

  // View counts
  const todayCount = allTodos.filter(t => !t.isCompleted && (isToday(t.dueDate) || isOverdue(t.dueDate) || (!t.dueDate && t.priority <= 2))).length;
  const weekCount = allTodos.filter(t => !t.isCompleted && isThisWeek(t.dueDate)).length;
  const backlogCount = allTodos.filter(t => !t.isCompleted && (t.priority >= 5 || (!t.dueDate && t.priority >= 3))).length;
  const overdueCount = allTodos.filter(t => !t.isCompleted && isOverdue(t.dueDate)).length;

  // Render single todo item
  const renderTodoItem = (todo) => {
    const style = getPriorityStyle(todo.priority);
    const isExpanded = expandedTodo === todo._id;
    const overdue = isOverdue(todo.dueDate) && !todo.isCompleted;
    
    return (
      <div
        key={todo._id}
        data-testid={`todo-item-${todo._id}`}
        className={`group rounded-xl border transition-all ${
          overdue ? 'bg-red-500/5 border-red-500/30' : `${style.bg} ${style.border}`
        } ${isExpanded ? 'ring-1 ring-amber-500/30' : ''}`}
      >
        <div className="flex items-start gap-3 p-3">
          {/* Checkbox */}
          <button
            onClick={() => handleComplete(todo)}
            data-testid={`todo-check-${todo._id}`}
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
              todo.isCompleted 
                ? `${style.dot} border-transparent` 
                : `border-current ${style.text} hover:scale-110`
            }`}
          >
            {todo.isCompleted && <CheckIcon size={10} color="#fff" />}
          </button>
          
          {/* Content */}
          <div 
            className="flex-1 min-w-0 cursor-pointer"
            onClick={() => setExpandedTodo(isExpanded ? null : todo._id)}
          >
            <p className={`text-sm leading-relaxed ${todo.isCompleted ? 'line-through text-white/30' : 'text-white/90'}`}>
              {todo.content}
            </p>
            
            {/* Meta info row */}
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {todo.dueDate && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  overdue ? 'bg-red-500/20 text-red-400' : 
                  isToday(todo.dueDate) ? 'bg-amber-500/20 text-amber-400' : 
                  'bg-white/5 text-white/40'
                }`}>
                  {formatRelativeDate(todo.dueDate)}
                </span>
              )}
              {todo.recurrence && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center gap-1">
                  <RecurIcon size={12} color="currentColor" />
                  {RECURRENCE_OPTIONS.find(r => r.value === todo.recurrence)?.label}
                </span>
              )}
              {todo.image && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-400 flex items-center gap-1">
                  <PhotoIcon size={12} color="currentColor" />
                  Image
                </span>
              )}
            </div>
            
            {/* Image preview */}
            {todo.image && (
              <div className="mt-2 relative group/img">
                <img 
                  src={todo.image} 
                  alt="Attachment"
                  className="max-w-[180px] max-h-[100px] rounded-lg object-cover border border-white/10"
                />
                <button
                  onClick={(e) => { e.stopPropagation(); onUpdateItem(todo._id, { image: null }); }}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity"
                >
                  <CloseIcon size={10} color="#fff" />
                </button>
              </div>
            )}
          </div>
          
          {/* Quick actions (visible on hover) */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <input
              type="file"
              accept="image/*"
              ref={el => fileInputRef.current[todo._id] = el}
              onChange={(e) => handleImageUpload(todo._id, e)}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current[todo._id]?.click()}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              title="Add image"
            >
              <PhotoIcon size={14} color="#EC4899" />
            </button>
            <button
              onClick={() => onDeleteItem(todo._id)}
              className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors"
              title="Delete"
            >
              <TrashIcon size={14} color="#EF4444" />
            </button>
          </div>
        </div>
        
        {/* Expanded edit panel */}
        {isExpanded && (
          <div className="px-3 pb-3 pt-2 border-t border-white/5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {/* Priority */}
              <div>
                <label className="text-xs text-white/40 mb-1 block">Priority</label>
                <select
                  value={todo.priority || 4}
                  onChange={(e) => onUpdateItem(todo._id, { priority: Number(e.target.value) })}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                >
                  <option value={1}>P1 - Urgent & Important</option>
                  <option value={2}>P2 - Important</option>
                  <option value={3}>P3 - Scheduled</option>
                  <option value={4}>P4 - Normal</option>
                  <option value={5}>P5 - Backlog</option>
                  <option value={6}>P6 - Someday</option>
                </select>
              </div>
              
              {/* Due Date */}
              <div>
                <label className="text-xs text-white/40 mb-1 block">Due Date</label>
                <input
                  type="date"
                  value={todo.dueDate || ''}
                  onChange={(e) => onUpdateItem(todo._id, { dueDate: e.target.value || null })}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
            </div>
            
            {/* Recurrence */}
            <div>
              <label className="text-xs text-white/40 mb-1 block">Repeat</label>
              <select
                value={todo.recurrence || ''}
                onChange={(e) => onUpdateItem(todo._id, { recurrence: e.target.value || null })}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              >
                {RECURRENCE_OPTIONS.map(opt => (
                  <option key={opt.value || 'none'} value={opt.value || ''}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render matrix view (Eisenhower)
  const renderMatrixView = () => {
    const quadrants = [
      { priority: 1, title: 'Do First', subtitle: 'Urgent & Important', color: 'red' },
      { priority: 2, title: 'Schedule', subtitle: 'Important, Not Urgent', color: 'amber' },
      { priority: 3, title: 'Delegate', subtitle: 'Urgent, Not Important', color: 'blue' },
      { priority: 4, title: 'Consider', subtitle: 'Not Urgent, Not Important', color: 'slate' },
    ];
    
    return (
      <div className="grid grid-cols-2 gap-3 p-4">
        {quadrants.map(q => {
          const qTodos = filteredTodos.filter(t => t.priority === q.priority);
          return (
            <div key={q.priority} className={`rounded-xl border border-${q.color}-500/20 bg-${q.color}-500/5 p-3`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-2 h-2 rounded-full bg-${q.color}-500`} />
                <span className={`text-sm font-medium text-${q.color}-400`}>{q.title}</span>
                <span className="text-xs text-white/30">({qTodos.length})</span>
              </div>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {qTodos.length === 0 ? (
                  <p className="text-xs text-white/20 text-center py-4">No items</p>
                ) : (
                  qTodos.map(todo => renderTodoItem(todo))
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-zinc-900/95 rounded-2xl border border-white/10 overflow-hidden" data-testid="todo-list-view">
      {/* Header - Clean & Minimal */}
      <div className="px-5 py-4 border-b border-white/5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 flex items-center justify-center">
              <AllTasksIcon size={20} color="#F59E0B" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-white">Tasks</h2>
              <p className="text-xs text-white/40">{allTodos.filter(t => !t.isCompleted).length} active</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {overdueCount > 0 && (
              <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400">
                <FireIcon size={12} color="#EF4444" />
                {overdueCount} overdue
              </span>
            )}
            <button
              onClick={() => setShowCompleted(!showCompleted)}
              className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${
                showCompleted ? 'bg-white/10 text-white' : 'bg-white/5 text-white/40'
              }`}
            >
              {showCompleted ? 'Hide Done' : 'Show Done'}
            </button>
          </div>
        </div>
        
        {/* View Tabs - Mobile-first with custom icons */}
        <div className="flex gap-1 p-1 bg-white/5 rounded-xl overflow-x-auto">
          {[
            { id: 'today', label: 'Today', count: todayCount, Icon: SunriseIcon, color: '#F59E0B' },
            { id: 'week', label: 'This Week', count: weekCount, Icon: WeekIcon, color: '#3B82F6' },
            { id: 'matrix', label: 'Matrix', count: null, Icon: MatrixIcon, color: '#8B5CF6' },
            { id: 'backlog', label: 'Backlog', count: backlogCount, Icon: BacklogIcon, color: '#6B7280' },
            { id: 'all', label: 'All', count: allTodos.filter(t => !t.isCompleted).length, Icon: AllTasksIcon, color: '#10B981' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              data-testid={`todo-tab-${tab.id}`}
              className={`flex items-center gap-1.5 flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeView === tab.id 
                  ? 'bg-amber-500/20 text-amber-400' 
                  : 'text-white/50 hover:text-white/70 hover:bg-white/5'
              }`}
            >
              <tab.Icon size={16} color={activeView === tab.id ? tab.color : 'currentColor'} />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.count !== null && <span className="opacity-60">({tab.count})</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        {activeView === 'matrix' ? (
          renderMatrixView()
        ) : (
          <div className="p-4 space-y-2">
            {filteredTodos.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-3">
                  {activeView === 'today' ? <SunriseIcon size={28} color="#ffffff30" /> :
                   activeView === 'backlog' ? <BacklogIcon size={28} color="#ffffff30" /> :
                   activeView === 'week' ? <WeekIcon size={28} color="#ffffff30" /> :
                   activeView === 'matrix' ? <MatrixIcon size={28} color="#ffffff30" /> :
                   <AllTasksIcon size={28} color="#ffffff30" />}
                </div>
                <p className="text-sm text-white/30">
                  {activeView === 'today' ? 'Nothing due today!' : 
                   activeView === 'backlog' ? 'No backlog items' : 
                   'No tasks'}
                </p>
              </div>
            ) : (
              filteredTodos.map(todo => renderTodoItem(todo))
            )}
          </div>
        )}
      </ScrollArea>

      {/* Add Task - Clean input */}
      <div className="p-4 border-t border-white/5">
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-2 rounded-xl bg-white/5 border border-white/10">
            <div className="w-5 h-5 rounded-full border-2 border-amber-500/40 flex items-center justify-center">
              <PlusIcon size={10} color="#F59E0B" />
            </div>
            <Input
              value={newTodo}
              onChange={(e) => setNewTodo(e.target.value)}
              placeholder="Add a task..."
              data-testid="todo-input"
              className="flex-1 bg-transparent border-0 text-sm text-white placeholder:text-white/30 p-0 h-auto focus-visible:ring-0"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) handleAddTodo();
              }}
            />
            <Button
              onClick={handleAddTodo}
              disabled={!newTodo.trim()}
              size="sm"
              data-testid="todo-add-btn"
              className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30"
            >
              Add
            </Button>
          </div>
          
          {/* Optional: Due date and recurrence for new task */}
          {newTodo.trim() && (
            <div className="flex items-center gap-2 pl-7">
              <input
                type="date"
                value={newTodoDueDate}
                onChange={(e) => setNewTodoDueDate(e.target.value)}
                placeholder="Due date"
                className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs text-white/70"
              />
              <select
                value={newTodoRecurrence || ''}
                onChange={(e) => setNewTodoRecurrence(e.target.value || null)}
                className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs text-white/70"
              >
                {RECURRENCE_OPTIONS.map(opt => (
                  <option key={opt.value || 'none'} value={opt.value || ''}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// WEEK CALENDAR VIEW
// ============================================
function WeekCalendar({ events, projectId, onAddEvent }) {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  
  const getWeekDays = () => {
    const days = [];
    const start = new Date(currentWeek);
    start.setDate(start.getDate() - start.getDay());
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const weekDays = getWeekDays();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getEventsForDay = (date) => {
    return events.filter(e => {
      const eventDate = new Date(e.date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  return (
    <div className="bg-black/40 rounded-xl border border-white/10 p-4">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={() => {
          const newDate = new Date(currentWeek);
          newDate.setDate(newDate.getDate() - 7);
          setCurrentWeek(newDate);
        }}>
          <ArrowIcon size={16} color="#9CA3AF" direction="left" />
        </Button>
        <span className="text-white font-medium">
          {weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
        <Button variant="ghost" size="sm" onClick={() => {
          const newDate = new Date(currentWeek);
          newDate.setDate(newDate.getDate() + 7);
          setCurrentWeek(newDate);
        }}>
          <ArrowIcon size={16} color="#9CA3AF" direction="right" />
        </Button>
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((day, idx) => {
          const dayEvents = getEventsForDay(day);
          const isToday = day.toDateString() === new Date().toDateString();
          
          return (
            <div 
              key={idx}
              className={`
                min-h-[100px] rounded-lg p-2 
                ${isToday ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-white/5'}
              `}
            >
              <div className="text-center mb-2">
                <div className="text-xs text-white/40">{dayNames[idx]}</div>
                <div className={`text-lg font-semibold ${isToday ? 'text-amber-400' : 'text-white/80'}`}>
                  {day.getDate()}
                </div>
              </div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((event, eIdx) => (
                  <div 
                    key={eIdx}
                    className="w-full h-5 rounded text-[10px] px-1 truncate flex items-center text-black font-medium"
                    style={{ backgroundColor: event.color || '#FBBF24' }}
                  >
                    {event.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[10px] text-white/40 text-center">
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// LINKS & DOCUMENTS PANEL
// ============================================
function LinksDocumentsPanel({ items, keywords, selectedKeyword, onSelectKeyword, onAddItem }) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', url: '', type: 'link', keywords: [] });

  const filteredItems = selectedKeyword 
    ? items.filter(i => i.keywords?.includes(selectedKeyword))
    : items;

  const handleAdd = () => {
    onAddItem(newItem);
    setNewItem({ title: '', url: '', type: 'link', keywords: [] });
    setShowAddDialog(false);
  };

  return (
    <div className="bg-black/40 rounded-xl border border-white/10 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookIcon size={18} color="#FBBF24" />
          <span className="font-medium text-white">Links & Documents</span>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-black">
              <PlusIcon size={14} color="#000" className="mr-1" /> Add
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Add Link or Document</DialogTitle>
              <DialogDescription className="text-white/60">Add a new link or document to your collection</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Input 
                placeholder="Title" 
                value={newItem.title}
                onChange={(e) => setNewItem({...newItem, title: e.target.value})}
                className="bg-white/5 border-white/10"
              />
              <Input 
                placeholder="URL" 
                value={newItem.url}
                onChange={(e) => setNewItem({...newItem, url: e.target.value})}
                className="bg-white/5 border-white/10"
              />
              <Input 
                placeholder="Keywords (comma separated)" 
                onChange={(e) => setNewItem({...newItem, keywords: e.target.value.split(',').map(k => k.trim())})}
                className="bg-white/5 border-white/10"
              />
              <Button onClick={handleAdd} className="w-full bg-amber-500 hover:bg-amber-400 text-black">
                Add
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {keywords.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          <Badge 
            variant={!selectedKeyword ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => onSelectKeyword(null)}
          >
            All
          </Badge>
          {keywords.map((kw, idx) => (
            <Badge 
              key={idx}
              variant={selectedKeyword === kw ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => onSelectKeyword(kw)}
            >
              {kw}
            </Badge>
          ))}
        </div>
      )}

      <ScrollArea className="h-[200px]">
        {filteredItems.length === 0 ? (
          <p className="text-white/40 text-center py-8">No items yet</p>
        ) : (
          <div className="space-y-2">
            {filteredItems.map((item, idx) => (
              <a 
                key={idx}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
              >
                {item.type === 'link' ? (
                  <ShareIcon size={16} color="#06B6D4" />
                ) : (
                  <BookIcon size={16} color="#F472B6" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate group-hover:text-amber-400">{item.title}</p>
                  <p className="text-xs text-white/40 truncate">{item.url}</p>
                </div>
                <ArrowIcon size={14} color="#9CA3AF" direction="right" className="opacity-0 group-hover:opacity-100" />
              </a>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

// ============================================
// MAIN SKETCHHUB COMPONENT
// ============================================
export default function SketchHub() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');
  
  // Auth state
  const [user, setUser] = useState(null);
  
  // View state
  const [activeView, setActiveView] = useState('dashboard'); // 'dashboard', 'split', 'messages', 'canvas', 'calendar', 'links'
  const [showQuadrants, setShowQuadrants] = useState(true);
  const [messagingFullscreen, setMessagingFullscreen] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Data state
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [canvasItems, setCanvasItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [linksDocuments, setLinksDocuments] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  
  // Selection state
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Quick message state for "message first" vibe
  const [quickMessage, setQuickMessage] = useState('');
  const [quickMessages, setQuickMessages] = useState([]);
  
  // Drawing state for canvas annotations
  const [drawings, setDrawings] = useState([]);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  
  // Team members for project
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Invite dialog state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [projectInvites, setProjectInvites] = useState([]);
  
  // Message organization state
  const [messageView, setMessageView] = useState('all'); // 'all', 'byDate', 'byThread'
  const [messageFilter, setMessageFilter] = useState('');

  // Mobile swipe navigation state
  const [swipeStart, setSwipeStart] = useState(null);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  
  // NVC Canvas Overlay state
  const [showNVCOverlay, setShowNVCOverlay] = useState(false);
  const [activeNVCQuadrant, setActiveNVCQuadrant] = useState(null);
  const [nvcQuickInputOpen, setNvcQuickInputOpen] = useState(false);
  const [nvcQuickInputValue, setNvcQuickInputValue] = useState('');
  const [nvcContentByQuadrant, setNvcContentByQuadrant] = useState({
    observations: [],
    feelings: [],
    needs: [],
    requests: []
  });
  
  // M.ai.ry Wisdom Toolbar state
  const [showMairyWisdom, setShowMairyWisdom] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(true); // Default to true for now - can be controlled via settings
  
  // Check if mobile device
  useEffect(() => {
    const checkMobile = () => setIsMobileDevice(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // View order for swipe navigation
  const viewOrder = ['messages', 'todos', 'canvas', 'calendar'];
  
  // Swipe navigation handlers
  const handleSwipeStart = (e) => {
    if (!isMobileDevice || activeView === 'dashboard') return;
    const touch = e.touches[0];
    setSwipeStart({ x: touch.clientX, y: touch.clientY, time: Date.now() });
  };
  
  const handleSwipeEnd = (e) => {
    if (!swipeStart || !isMobileDevice || activeView === 'dashboard') return;
    
    const touch = e.changedTouches[0];
    const dx = touch.clientX - swipeStart.x;
    const dy = touch.clientY - swipeStart.y;
    const dt = Date.now() - swipeStart.time;
    
    // Only count as swipe if horizontal movement > vertical and fast enough
    const minSwipeDistance = 80;
    const maxSwipeTime = 500;
    
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > minSwipeDistance && dt < maxSwipeTime) {
      const currentIndex = viewOrder.indexOf(activeView);
      if (currentIndex === -1) {
        setSwipeStart(null);
        return;
      }
      
      if (dx > 0) {
        // Swipe right - go to previous view
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : viewOrder.length - 1;
        setActiveView(viewOrder[prevIndex]);
        toast.info(`← ${viewOrder[prevIndex].charAt(0).toUpperCase() + viewOrder[prevIndex].slice(1)}`, { duration: 1500 });
      } else {
        // Swipe left - go to next view
        const nextIndex = currentIndex < viewOrder.length - 1 ? currentIndex + 1 : 0;
        setActiveView(viewOrder[nextIndex]);
        toast.info(`${viewOrder[nextIndex].charAt(0).toUpperCase() + viewOrder[nextIndex].slice(1)} →`, { duration: 1500 });
      }
    }
    
    setSwipeStart(null);
  };

  // Load user from localStorage - check multiple possible keys
  useEffect(() => {
    // First try workbook user (from main login)
    const workbookUserId = localStorage.getItem('workbook_user_id');
    const workbookUserName = localStorage.getItem('workbook_user_name');
    const workbookUserEmail = localStorage.getItem('workbook_user_email');
    
    // Then try elchme user
    const elchmeUserId = localStorage.getItem('elchme_user_id');
    const elchmeUserName = localStorage.getItem('elchme_user_name');
    const elchmeUserEmail = localStorage.getItem('elchme_user_email');
    
    // Then try generic user object
    const storedUser = localStorage.getItem('user');
    
    if (workbookUserId && workbookUserName) {
      setUser({ _id: workbookUserId, name: workbookUserName, email: workbookUserEmail });
    } else if (elchmeUserId && elchmeUserName) {
      setUser({ _id: elchmeUserId, name: elchmeUserName, email: elchmeUserEmail });
    } else if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Load projects
  useEffect(() => {
    if (user) {
      loadProjects();
    }
  }, [user]);

  // Load project data when project changes
  useEffect(() => {
    if (projectId) {
      loadProjectData(projectId);
      setActiveView('messages'); // Default to messaging - user-friendly first experience
    } else {
      setActiveView('dashboard');
    }
  }, [projectId]);

  const loadProjects = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/sketch/projects?user_id=${user._id}`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Failed to load projects');
    }
  };

  const loadProjectData = async (pid) => {
    try {
      const projectRes = await fetch(`${BACKEND_URL}/api/sketch/projects/${pid}`);
      if (projectRes.ok) {
        const projectData = await projectRes.json();
        setCurrentProject(projectData);
      }

      const msgRes = await fetch(`${BACKEND_URL}/api/sketch/projects/${pid}/messages`);
      if (msgRes.ok) {
        const msgData = await msgRes.json();
        setMessages(msgData.messages || []);
      }

      const itemsRes = await fetch(`${BACKEND_URL}/api/sketch/projects/${pid}/items`);
      if (itemsRes.ok) {
        const itemsData = await itemsRes.json();
        setCanvasItems(itemsData.items || []);
        
        const allKeywords = new Set();
        (itemsData.items || []).forEach(item => {
          (item.keywords || []).forEach(k => allKeywords.add(k));
        });
        setKeywords(Array.from(allKeywords));
      }

      const eventsRes = await fetch(`${BACKEND_URL}/api/sketch/projects/${pid}/events`);
      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setEvents(eventsData.events || []);
      }

      const linksRes = await fetch(`${BACKEND_URL}/api/sketch/projects/${pid}/links`);
      if (linksRes.ok) {
        const linksData = await linksRes.json();
        setLinksDocuments(linksData.links || []);
      }

      // Load canvas drawings
      const drawingsRes = await fetch(`${BACKEND_URL}/api/sketch/projects/${pid}/drawings`);
      if (drawingsRes.ok) {
        const drawingsData = await drawingsRes.json();
        // Map backend field names to frontend field names
        const mappedDrawings = (drawingsData.drawings || []).map(d => ({
          id: d._id,
          type: d.type,
          color: d.color,
          brushSize: d.brush_size,
          path: d.path,
          x: d.x,
          y: d.y,
          width: d.width,
          height: d.height,
          offsetX: d.offset_x || 0,
          offsetY: d.offset_y || 0
        }));
        setDrawings(mappedDrawings);
      }
    } catch (error) {
      console.error('Failed to load project data:', error);
    }
  };

  // Canvas Drawing CRUD operations
  const saveDrawing = async (drawing) => {
    if (!projectId) return null;
    try {
      const response = await fetch(`${BACKEND_URL}/api/sketch/projects/${projectId}/drawings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId,
          type: drawing.type,
          color: drawing.color,
          brush_size: drawing.brushSize || 2,
          path: drawing.path || null,
          x: drawing.x || null,
          y: drawing.y || null,
          width: drawing.width || null,
          height: drawing.height || null,
          offset_x: drawing.offsetX || 0,
          offset_y: drawing.offsetY || 0
        })
      });
      if (response.ok) {
        const data = await response.json();
        return data.id;
      }
    } catch (error) {
      console.error('Failed to save drawing:', error);
    }
    return null;
  };

  const updateDrawingPosition = async (drawingId, offsetX, offsetY) => {
    try {
      await fetch(`${BACKEND_URL}/api/sketch/drawings/${drawingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offset_x: offsetX, offset_y: offsetY })
      });
    } catch (error) {
      console.error('Failed to update drawing position:', error);
    }
  };

  const deleteDrawing = async (drawingId) => {
    try {
      await fetch(`${BACKEND_URL}/api/sketch/drawings/${drawingId}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Failed to delete drawing:', error);
    }
  };

  const clearAllDrawings = async () => {
    if (!projectId) return;
    try {
      await fetch(`${BACKEND_URL}/api/sketch/projects/${projectId}/drawings`, {
        method: 'DELETE'
      });
      setDrawings([]);
    } catch (error) {
      console.error('Failed to clear drawings:', error);
    }
  };

  // Handle drawing changes from canvas - this syncs with backend
  const handleDrawingChange = async (newDrawings) => {
    // Find new drawings (ones without a backend id)
    const currentIds = new Set(drawings.map(d => d.id));
    const newDrawing = newDrawings.find(d => !currentIds.has(d.id) && !d.id?.includes('-'));
    
    if (newDrawing) {
      // This is a newly created drawing, save it
      const backendId = await saveDrawing(newDrawing);
      if (backendId) {
        // Update local state with backend ID
        const updatedDrawings = newDrawings.map(d => 
          d === newDrawing ? { ...d, id: backendId } : d
        );
        setDrawings(updatedDrawings);
        return;
      }
    }
    
    // Check for position changes (moved drawings)
    for (const newD of newDrawings) {
      const oldD = drawings.find(d => d.id === newD.id);
      if (oldD && (oldD.offsetX !== newD.offsetX || oldD.offsetY !== newD.offsetY)) {
        await updateDrawingPosition(newD.id, newD.offsetX, newD.offsetY);
      }
    }
    
    // Check for deletions
    for (const oldD of drawings) {
      if (!newDrawings.find(d => d.id === oldD.id)) {
        await deleteDrawing(oldD.id);
      }
    }
    
    setDrawings(newDrawings);
  };

  const handleCreateProject = async (projectData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/sketch/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...projectData,
          status: 'active',
          progress: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // If template has default items, create them
        if (projectData.template && PROJECT_TEMPLATES[projectData.template]?.defaultItems) {
          const template = PROJECT_TEMPLATES[projectData.template];
          for (const item of template.defaultItems) {
            await fetch(`${BACKEND_URL}/api/sketch/items`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                project_id: data.id,
                ...item,
                x: Math.random() * 300 + 50,
                y: Math.random() * 200 + 50,
                created_at: new Date().toISOString()
              })
            });
          }
        }
        
        navigate(`/sketch?project=${data.id}`);
        loadProjects();
      }
    } catch (error) {
      throw error;
    }
  };

  const handleSelectProject = (project) => {
    navigate(`/sketch?project=${project._id}`);
  };

  const handleSendMessage = async (content) => {
    if (!user || !projectId) return;
    
    const newMsg = {
      project_id: projectId,
      sender_id: user._id,
      sender_name: user.name || user.email,
      content,
      created_at: new Date().toISOString()
    };

    try {
      const response = await fetch(`${BACKEND_URL}/api/sketch/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newMsg)
      });
      
      if (response.ok) {
        setMessages(prev => [...prev, newMsg]);
      }
    } catch (error) {
      throw error;
    }
  };

  // Update message with keywords, priority, assignee, etc.
  const handleUpdateMessage = async (messageId, updates) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/sketch/messages/${messageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        setMessages(prev => prev.map(msg => 
          msg._id === messageId ? { ...msg, ...updates } : msg
        ));
        toast.success('Message updated');
      }
    } catch (error) {
      console.error('Failed to update message:', error);
    }
  };

  // Convert message to todo (creates a canvas item and marks message as todo)
  const handleConvertMessageToTodo = async (message) => {
    try {
      // Update message to mark as todo
      await handleUpdateMessage(message._id, { isTodo: true, isCompleted: false });
      
      // Also add to canvas as a card
      const newItem = {
        project_id: projectId,
        type: 'todo',
        content: message.content,
        x: Math.random() * 400 + 50,
        y: Math.random() * 300 + 50,
        quadrant: 'urgentImportant',
        shape: 'card',
        isTodo: true,
        isCompleted: false,
        status: 'now',
        source_message_id: message._id,
        created_at: new Date().toISOString()
      };

      const response = await fetch(`${BACKEND_URL}/api/sketch/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newItem)
      });
      
      if (response.ok) {
        const data = await response.json();
        setCanvasItems(prev => [...prev, { ...newItem, _id: data.id }]);
        toast.success('Converted to todo!');
      }
    } catch (error) {
      toast.error('Failed to convert to todo');
    }
  };

  const handleDropMessage = async (message, x, y, quadrant) => {
    const newItem = {
      project_id: projectId,
      type: 'message',
      content: message.content,
      x,
      y,
      quadrant,
      source_message_id: message._id,
      created_at: new Date().toISOString()
    };

    try {
      const response = await fetch(`${BACKEND_URL}/api/sketch/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newItem)
      });
      
      if (response.ok) {
        const data = await response.json();
        setCanvasItems(prev => [...prev, { ...newItem, _id: data.id }]);
        toast.success('Added to canvas!');
      }
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    try {
      await fetch(`${BACKEND_URL}/api/sketch/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      setCanvasItems(prev => prev.map(item => 
        item._id === itemId ? { ...item, ...updates } : item
      ));
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await fetch(`${BACKEND_URL}/api/sketch/items/${itemId}`, {
        method: 'DELETE'
      });
      
      setCanvasItems(prev => prev.filter(item => item._id !== itemId));
      toast.success('Item removed');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Quick message handler for "message first" dashboard experience
  const handleQuickMessage = () => {
    if (quickMessage.trim()) {
      setQuickMessages(prev => [quickMessage.trim(), ...prev]);
      setQuickMessage('');
      toast.success('Thought captured! Drag it to a project or create one.');
    }
  };

  // Add canvas item from various sources (quick thoughts, etc.)
  const handleAddCanvasItem = async (itemData) => {
    if (!projectId) {
      toast.error('Select a project first');
      return;
    }
    
    const newItem = {
      project_id: projectId,
      type: itemData.type || 'note',
      content: itemData.content,
      x: itemData.x || Math.random() * 400 + 50,
      y: itemData.y || Math.random() * 300 + 50,
      quadrant: itemData.quadrant || 'urgentImportant',
      shape: itemData.shape || 'sticky',
      isTodo: itemData.isTodo || false,
      isCompleted: false,
      dueDate: itemData.dueDate || null,
      assignee: itemData.assignee || null,
      status: 'now',
      created_at: new Date().toISOString()
    };

    try {
      const response = await fetch(`${BACKEND_URL}/api/sketch/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newItem)
      });
      
      if (response.ok) {
        const data = await response.json();
        setCanvasItems(prev => [...prev, { ...newItem, _id: data.id }]);
        toast.success('Added to canvas!');
      }
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleAddLink = async (linkData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/sketch/links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...linkData, project_id: projectId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setLinksDocuments(prev => [...prev, { ...linkData, _id: data.id }]);
        toast.success('Link added!');
      }
    } catch (error) {
      toast.error('Failed to add link');
    }
  };

  // NVC Quick Input save handler
  const handleSaveNVCContent = async (content) => {
    if (!content.trim() || !activeNVCQuadrant) return;
    
    // Add content to the NVC quadrant
    setNvcContentByQuadrant(prev => ({
      ...prev,
      [activeNVCQuadrant]: [...(prev[activeNVCQuadrant] || []), content]
    }));
    
    // Also create a canvas item in the corresponding Eisenhower quadrant
    const quadrantMapping = {
      observations: 'urgentImportant',
      feelings: 'importantNotUrgent', 
      needs: 'importantNotUrgent',
      requests: 'urgentNotImportant'
    };
    
    const nvcLabels = {
      observations: 'Observation',
      feelings: 'Feeling',
      needs: 'Need',
      requests: 'Request'
    };
    
    await handleAddCanvasItem({
      content: `[${nvcLabels[activeNVCQuadrant]}] ${content}`,
      type: 'note',
      quadrant: quadrantMapping[activeNVCQuadrant],
      nvcType: activeNVCQuadrant
    });
    
    setNvcQuickInputValue('');
    setActiveNVCQuadrant(null);
    toast.success(`${nvcLabels[activeNVCQuadrant]} added to canvas!`);
  };

  // ============================================
  // INVITE SYSTEM - One-Click Sharing
  // ============================================
  const generateInviteLink = async () => {
    if (!currentProject) return;
    
    setInviteLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: currentProject._id,
          project_name: currentProject.name,
          role: 'member',
          expires_hours: 168 // 7 days
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setInviteLink(data.link);
        loadProjectInvites();
        toast.success('Invite link generated!');
      } else {
        throw new Error('Failed to generate invite');
      }
    } catch (error) {
      toast.error('Failed to generate invite link');
    } finally {
      setInviteLoading(false);
    }
  };

  const loadProjectInvites = async () => {
    if (!currentProject) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/invites/${currentProject._id}`);
      if (response.ok) {
        const data = await response.json();
        setProjectInvites(data.invites || []);
      }
    } catch (error) {
      console.error('Failed to load invites');
    }
  };

  const revokeInvite = async (inviteId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invites/${inviteId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Invite revoked');
        loadProjectInvites();
      }
    } catch (error) {
      toast.error('Failed to revoke invite');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  // Filter projects
  const filteredProjects = projects.filter(p => {
    const matchesSearch = !searchQuery || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || p.type === filterType;
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-white/60 hover:text-white"
            >
              <HomeIcon size={18} color="currentColor" />
            </Button>
            <div className="h-6 w-px bg-white/10" />
            
            {activeView === 'dashboard' ? (
              <h1 className="text-xl font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Sketch Hub
              </h1>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/sketch')}
                  className="text-white/60 hover:text-white"
                >
                  <ArrowIcon size={16} color="currentColor" direction="left" className="mr-1" />
                  Projects
                </Button>
                {currentProject && (
                  <>
                    <span className="text-white font-medium">{currentProject.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowInviteDialog(true);
                        loadProjectInvites();
                      }}
                      className="text-amber-400/80 hover:text-amber-300 hover:bg-amber-500/10"
                      data-testid="share-project-btn"
                    >
                      <ShareIcon size={14} color="currentColor" className="mr-1" />
                      Share
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
          
          {/* View Toggle / Actions */}
          <div className="flex items-center gap-2">
            {activeView !== 'dashboard' && (
              <Tabs value={activeView} onValueChange={setActiveView}>
                <TabsList className="bg-black/30 backdrop-blur-xl border border-white/10">
                  <TabsTrigger value="messages" className="data-[state=active]:bg-amber-500/30 data-[state=active]:text-amber-400">
                    <MessageIcon size={14} color="currentColor" />
                  </TabsTrigger>
                  <TabsTrigger value="todos" className="data-[state=active]:bg-amber-500/30 data-[state=active]:text-amber-400">
                    <CheckIcon size={14} color="currentColor" />
                  </TabsTrigger>
                  <TabsTrigger value="canvas" className="data-[state=active]:bg-amber-500/30 data-[state=active]:text-amber-400">
                    <SparkleIcon size={14} color="currentColor" />
                  </TabsTrigger>
                  <TabsTrigger value="board" className="data-[state=active]:bg-amber-500/30 data-[state=active]:text-amber-400">
                    <StackIcon size={14} color="currentColor" />
                  </TabsTrigger>
                  <TabsTrigger value="calendar" className="data-[state=active]:bg-amber-500/30 data-[state=active]:text-amber-400">
                    <CalendarIcon size={14} color="currentColor" />
                  </TabsTrigger>
                  <TabsTrigger value="split" className="data-[state=active]:bg-amber-500/30 data-[state=active]:text-amber-400">
                    <UsersIcon size={14} color="currentColor" />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}
            
            <Button 
              size="sm" 
              onClick={() => setShowCreateDialog(true)}
              className="bg-amber-500 hover:bg-amber-400 text-black"
            >
              <PlusIcon size={14} color="#000" className="mr-1" /> New Project
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main 
        className="p-4"
        onTouchStart={handleSwipeStart}
        onTouchEnd={handleSwipeEnd}
      >
        {!user ? (
          <div className="flex flex-col items-center justify-center h-[60vh] text-center">
            <SparkleIcon size={64} color="rgba(255,255,255,0.2)" />
            <h2 className="text-2xl font-bold text-white mt-6">Welcome to Sketch Hub</h2>
            <p className="text-white/60 mt-2 max-w-md">
              Your epic project management solution for conflict resolution, family projects, and more.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="mt-6 bg-amber-500 hover:bg-amber-400 text-black"
            >
              Sign In
            </Button>
          </div>
        ) : activeView === 'dashboard' ? (
          /* Dashboard View */
          <div className="max-w-7xl mx-auto">
            {/* Quick Message Panel - Message First Vibe */}
            <div className="mb-6">
              <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/10 via-cyan-500/5 to-purple-500/10 border border-amber-500/20">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500/30 to-orange-500/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                    <MessageIcon size={22} color="#F59E0B" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Quick Thought Capture
                    </h3>
                    <p className="text-white/50 text-sm mb-3">
                      Capture your thoughts here, then drag them to projects later
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={quickMessage}
                        onChange={(e) => setQuickMessage(e.target.value)}
                        placeholder="What's on your mind? Type a feeling, thought, or idea..."
                        className="flex-1 bg-black/30 border-white/10 text-white placeholder:text-white/30 focus:border-amber-500/50"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && quickMessage.trim()) {
                            handleQuickMessage();
                          }
                        }}
                      />
                      <Button 
                        onClick={handleQuickMessage}
                        disabled={!quickMessage.trim()}
                        className="bg-amber-500 hover:bg-amber-400 text-black px-4"
                      >
                        <ArrowIcon size={18} color="#000" direction="right" />
                      </Button>
                    </div>
                    {quickMessages.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {quickMessages.slice(0, 5).map((msg, idx) => (
                          <div 
                            key={idx}
                            className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 text-sm flex items-center gap-2 cursor-move hover:border-amber-500/30 hover:bg-amber-500/10 transition-colors"
                            draggable
                            onDragStart={(e) => e.dataTransfer.setData('text/plain', msg)}
                          >
                            <span>{msg.length > 30 ? msg.substring(0, 30) + '...' : msg}</span>
                            <button 
                              onClick={() => setQuickMessages(prev => prev.filter((_, i) => i !== idx))}
                              className="text-white/30 hover:text-red-400"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        {quickMessages.length > 5 && (
                          <span className="text-white/40 text-sm px-2 py-1">+{quickMessages.length - 5} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <SearchIcon size={18} color="rgba(255,255,255,0.4)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search projects..."
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="all" className="text-white">All Types</SelectItem>
                  {Object.values(PROJECT_TYPES).map(type => (
                    <SelectItem key={type.id} value={type.id} className="text-white">
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px] bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="all" className="text-white">All Status</SelectItem>
                  {Object.entries(PROJECT_STATUS).map(([key, status]) => (
                    <SelectItem key={key} value={key} className="text-white">
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Projects Grid */}
            {filteredProjects.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <BookIcon size={64} color="rgba(255,255,255,0.2)" />
                <h3 className="text-xl font-semibold text-white mt-6">No projects yet</h3>
                <p className="text-white/60 mt-2 max-w-md">
                  Create your first project to start organizing your thoughts, resolving conflicts, or managing family matters.
                </p>
                <Button 
                  onClick={() => setShowCreateDialog(true)}
                  className="mt-6 bg-amber-500 hover:bg-amber-400 text-black"
                >
                  <PlusIcon size={18} color="#000" className="mr-2" />
                  Create Your First Project
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProjects.map(project => (
                  <ProjectCard
                    key={project._id}
                    project={project}
                    onSelect={handleSelectProject}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Project View */
          <>
            {activeView === 'split' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-120px)]">
                <div className="md:col-span-2 rounded-xl border border-white/10 overflow-hidden">
                  <GraphPaperCanvas
                    items={canvasItems}
                    onUpdateItem={handleUpdateItem}
                    onDeleteItem={handleDeleteItem}
                    selectedItem={selectedItem}
                    onSelectItem={setSelectedItem}
                    onDropMessage={handleDropMessage}
                    teamMembers={teamMembers}
                    onAddItem={(itemData) => handleAddCanvasItem(itemData)}
                    drawings={drawings}
                    onDrawingChange={handleDrawingChange}
                    isDrawingMode={isDrawingMode}
                    setIsDrawingMode={setIsDrawingMode}
                    showNVCOverlay={showNVCOverlay}
                    onToggleNVCOverlay={() => setShowNVCOverlay(!showNVCOverlay)}
                    activeNVCQuadrant={activeNVCQuadrant}
                    onNVCQuadrantClick={(quadrant) => {
                      setActiveNVCQuadrant(quadrant === activeNVCQuadrant ? null : quadrant);
                      setNvcQuickInputOpen(true);
                    }}
                    nvcContentByQuadrant={nvcContentByQuadrant}
                  />
                </div>
                
                <div className="hidden md:block h-full">
                  <MessagingPanel
                    projectId={projectId}
                    userId={user._id}
                    userName={user.name || user.email}
                    messages={messages}
                    onSendMessage={handleSendMessage}
                    onRefresh={() => loadProjectData(projectId)}
                    selectedMessages={selectedMessages}
                    onSelectMessage={(msg) => {
                      setSelectedMessages(prev => 
                        prev.includes(msg._id) 
                          ? prev.filter(id => id !== msg._id)
                          : [...prev, msg._id]
                      );
                    }}
                    isFullscreen={false}
                    onToggleFullscreen={() => {}}
                    viewMode={messageView}
                    onViewModeChange={setMessageView}
                    onUpdateMessage={handleUpdateMessage}
                    onConvertToTodo={handleConvertMessageToTodo}
                    teamMembers={teamMembers}
                  />
                </div>
              </div>
            )}

            {activeView === 'messages' && (
              <div className="h-[calc(100vh-120px)]">
                <MessagingPanel
                  projectId={projectId}
                  userId={user._id}
                  userName={user.name || user.email}
                  messages={messages}
                  onSendMessage={handleSendMessage}
                  onRefresh={() => loadProjectData(projectId)}
                  selectedMessages={selectedMessages}
                  onSelectMessage={(msg) => {
                    setSelectedMessages(prev => 
                      prev.includes(msg._id) 
                        ? prev.filter(id => id !== msg._id)
                        : [...prev, msg._id]
                    );
                  }}
                  isFullscreen={messagingFullscreen}
                  onToggleFullscreen={() => setMessagingFullscreen(!messagingFullscreen)}
                  viewMode={messageView}
                  onViewModeChange={setMessageView}
                  onUpdateMessage={handleUpdateMessage}
                  onConvertToTodo={handleConvertMessageToTodo}
                  teamMembers={teamMembers}
                />
              </div>
            )}

            {activeView === 'canvas' && (
              <div className="h-[calc(100vh-120px)] rounded-xl border border-white/10 overflow-hidden">
                <GraphPaperCanvas
                  items={canvasItems}
                  onUpdateItem={handleUpdateItem}
                  onDeleteItem={handleDeleteItem}
                  selectedItem={selectedItem}
                  onSelectItem={setSelectedItem}
                  onDropMessage={handleDropMessage}
                  teamMembers={teamMembers}
                  onAddItem={(itemData) => handleAddCanvasItem(itemData)}
                  drawings={drawings}
                  onDrawingChange={setDrawings}
                  isDrawingMode={isDrawingMode}
                  setIsDrawingMode={setIsDrawingMode}
                  showNVCOverlay={showNVCOverlay}
                  onToggleNVCOverlay={() => setShowNVCOverlay(!showNVCOverlay)}
                  activeNVCQuadrant={activeNVCQuadrant}
                  onNVCQuadrantClick={(quadrant) => {
                    setActiveNVCQuadrant(quadrant === activeNVCQuadrant ? null : quadrant);
                    setNvcQuickInputOpen(true);
                  }}
                  nvcContentByQuadrant={nvcContentByQuadrant}
                />
              </div>
            )}

            {activeView === 'board' && (
              <div className="h-[calc(100vh-120px)] rounded-xl border border-white/10 overflow-hidden">
                <BoardView
                  items={canvasItems}
                  onUpdateItem={handleUpdateItem}
                  onDeleteItem={handleDeleteItem}
                  onAddItem={(itemData) => handleAddCanvasItem(itemData)}
                  teamMembers={teamMembers}
                />
              </div>
            )}

            {activeView === 'todos' && (
              <div className="h-[calc(100vh-120px)]">
                <TodoListView
                  items={canvasItems}
                  onUpdateItem={handleUpdateItem}
                  onDeleteItem={handleDeleteItem}
                  onAddItem={(itemData) => handleAddCanvasItem(itemData)}
                  messages={messages}
                />
              </div>
            )}

            {activeView === 'calendar' && (
              <WeekCalendar 
                events={events}
                projectId={projectId}
                onAddEvent={() => {}}
              />
            )}

            {activeView === 'links' && (
              <LinksDocumentsPanel
                items={linksDocuments}
                keywords={keywords}
                selectedKeyword={selectedKeyword}
                onSelectKeyword={setSelectedKeyword}
                onAddItem={handleAddLink}
              />
            )}
          </>
        )}
      </main>

      {/* Mobile Swipe Indicator - shows current view position */}
      {user && projectId && activeView !== 'dashboard' && isMobileDevice && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-black/60 backdrop-blur rounded-full">
            {viewOrder.map((view, idx) => (
              <div
                key={view}
                className={`w-2 h-2 rounded-full transition-all ${
                  activeView === view 
                    ? 'w-6 bg-amber-400' 
                    : 'bg-white/30'
                }`}
              />
            ))}
          </div>
          <p className="text-center text-white/40 text-xs mt-1">← Swipe to navigate →</p>
        </div>
      )}

      {/* Glass Floating Bottom Navigation */}
      {user && projectId && activeView !== 'dashboard' && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
          <div className="flex items-center gap-1 px-2 py-2 bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl">
            <button
              onClick={() => setActiveView('messages')}
              className={`p-3 rounded-xl transition-all ${activeView === 'messages' ? 'bg-amber-500/30 text-amber-400' : 'text-white/50 hover:text-white hover:bg-white/10'}`}
              title="Messages"
            >
              <MessageIcon size={20} color="currentColor" />
            </button>
            <button
              onClick={() => setActiveView('todos')}
              className={`p-3 rounded-xl transition-all ${activeView === 'todos' ? 'bg-amber-500/30 text-amber-400' : 'text-white/50 hover:text-white hover:bg-white/10'}`}
              title="Reminders"
            >
              <CheckIcon size={20} color="currentColor" />
            </button>
            <button
              onClick={() => setActiveView('canvas')}
              className={`p-3 rounded-xl transition-all ${activeView === 'canvas' ? 'bg-amber-500/30 text-amber-400' : 'text-white/50 hover:text-white hover:bg-white/10'}`}
              title="Canvas"
            >
              <SparkleIcon size={20} color="currentColor" />
            </button>
            <button
              onClick={() => setActiveView('calendar')}
              className={`p-3 rounded-xl transition-all ${activeView === 'calendar' ? 'bg-amber-500/30 text-amber-400' : 'text-white/50 hover:text-white hover:bg-white/10'}`}
              title="Calendar"
            >
              <CalendarIcon size={20} color="currentColor" />
            </button>
            <div className="w-px h-8 bg-white/10 mx-1" />
            <a
              href="/elchme"
              className="p-3 rounded-xl text-white/50 hover:text-purple-400 hover:bg-purple-500/10 transition-all"
              title="Empathetic Alchemy"
            >
              <FlowIcon size={20} color="currentColor" />
            </a>
            <a
              href="https://belongingsessions.com/belonging-ebook"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-xl text-white/50 hover:text-blue-400 hover:bg-blue-500/10 transition-all"
              title="Belonging E-Book"
            >
              <BookIcon size={20} color="currentColor" />
            </a>
          </div>
        </div>
      )}

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreateProject={handleCreateProject}
        userId={user?._id}
      />

      {/* Invite/Share Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              Share Project
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Generate a one-click invite link. Anyone with this link can instantly join "{currentProject?.name}".
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Generate New Link */}
            <div className="space-y-2">
              <Button
                onClick={generateInviteLink}
                disabled={inviteLoading}
                className="w-full bg-amber-500 hover:bg-amber-400 text-black"
                data-testid="generate-invite-btn"
              >
                {inviteLoading ? (
                  <>
                    <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <PlusIcon size={14} color="#000" className="mr-2" />
                    Generate New Invite Link
                  </>
                )}
              </Button>
            </div>

            {/* Current Invite Link */}
            {inviteLink && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-xs text-emerald-400 mb-2 font-semibold">New Invite Link (expires in 7 days)</p>
                <div className="flex gap-2">
                  <Input
                    value={inviteLink}
                    readOnly
                    className="flex-1 bg-black/30 border-white/10 text-white text-xs"
                    data-testid="invite-link-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(inviteLink)}
                    className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
                    data-testid="copy-invite-btn"
                  >
                    Copy
                  </Button>
                </div>
              </div>
            )}

            {/* Existing Invites */}
            {projectInvites.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-white/80">Active Invites</h4>
                <ScrollArea className="h-[150px]">
                  {projectInvites.map(invite => (
                    <div 
                      key={invite._id} 
                      className={`p-2 rounded-lg mb-2 flex items-center justify-between ${
                        invite.used_at ? 'bg-white/5 opacity-60' : 
                        invite.revoked ? 'bg-red-500/10 opacity-60' : 
                        'bg-white/10'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/60 truncate">
                          {invite._id.slice(0, 8)}...
                        </p>
                        <p className="text-[10px] text-white/40">
                          {invite.used_at ? 'Used' : invite.revoked ? 'Revoked' : 
                           `Expires ${new Date(invite.expires_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      {!invite.used_at && !invite.revoked && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => revokeInvite(invite._id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs"
                          data-testid={`revoke-invite-${invite._id}`}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInviteDialog(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NVC Quick Input Dialog */}
      <NVCQuickInput
        isOpen={nvcQuickInputOpen && activeNVCQuadrant}
        onClose={() => {
          setNvcQuickInputOpen(false);
          setNvcQuickInputValue('');
        }}
        quadrant={activeNVCQuadrant}
        value={nvcQuickInputValue}
        onChange={setNvcQuickInputValue}
        onSave={handleSaveNVCContent}
      />

      {/* M.ai.ry Wisdom Toolbar - Soul-centered project guidance */}
      <MairyFloatingButton 
        onClick={() => setShowMairyWisdom(true)}
        isHidden={!isSuperUser || !currentProject}
      />
      
      <MairyWisdomPanel
        isOpen={showMairyWisdom}
        onClose={() => setShowMairyWisdom(false)}
        projectData={currentProject}
        canvasItems={canvasItems}
        messages={messages}
      />
    </div>
  );
}
